import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AudienceCreateComponent,
  AudienceCreateFormValue,
  AudienceQuality
} from './create/audience-create.component';
import { resolveExpandSimilarUsers, resolvePayloadTags } from './create/audience-create-submission';
import { DestinationActivationModalComponent } from './destination-activation-modal/destination-activation-modal.component';
import { ActivationEditModalComponent, ActivationEditTarget } from './activation-edit-modal/activation-edit-modal.component';
import { AudienceExtractionsChartComponent } from './extractions-chart/audience-extractions-chart.component';
import { AudienceAdditionalInfoComponent } from './additional-info/audience-additional-info.component';
import { AudienceActivationPushChartComponent } from './activation-push-chart/audience-activation-push-chart.component';
import {
  AudienceApiResponse,
  AudienceApiService,
  AUDIENCE_TYPES,
  AudienceCreatePayload,
  AudienceFilterItem,
  AudienceIntersection,
  ActivationItemApi,
  AudienceUpdatePayload,
  GoalOption,
  CountryOption
} from '../../services/audience-api.service';
import { AuthService, SessionUser } from '../../services/auth.service';
import { UserPermissions } from '../../services/permissions';
import { Observable, Subscription, firstValueFrom, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, skip } from 'rxjs/operators';
import { NotificationService } from '../../services/notification.service';
import { NotificationBackendService } from '../../services/notification-backend.service';
import { CustomImportApiService, CustomImportDto } from '../../services/custom-import-api.service';
import { GroupsApiService, GroupDto } from '../../services/groups-api.service';
import { DestinationsApiService, DestinationDto } from '../../services/destinations-api.service';
import { getDestinationLogoPath } from '../../utils/destination-logos';
import { isSandboxError } from '../../utils/api-error';
import { EntityOwnerService } from '../../services/entity-owner.service';
import { ProviderDto, ProvidersApiService } from '../../services/providers-api.service';
import { EntityConfigService } from '../../services/entity-config.service';
import { NavigationBlockService, NavigationBlockOptions } from '../../services/navigation-block.service';
import { CreationStateService } from '../../services/creation-state.service';
import { ExperimentalFeaturesService } from '../../services/experimental-features.service';
import { environment } from '../../../environments/environment';
import { AudienceDestination, AudienceItem, AudienceStatus, FilterBuildResult } from './utils/audience.types';
import {
  addDays,
  formatDate,
  formatVolume,
  isInCurrentMonth,
  mapFrequency,
  normalizeDate,
  normalizeStatus,
  parseVolume,
  resolveEndDate,
  toNumericValue,
  todayIsoDate
} from './utils/audience-format';
import {
  buildInterestFilters,
  buildRuleBasedFilters,
  coerceFilterValueToArray,
  ensureSourceFiltersAreSerialized,
  extractEntityIdsFromFilterValue,
  FILTER_TYPES,
  inferHasIncompatibleFilters,
  mapFilterTypeToRuleGroup,
  normalizeFilterType,
  resolvePublisherToProvider
} from './utils/audience-filters';
import {
  mapCurveVolumeToPreviewAccuracy,
  mapPreviewAccuracyToForm
} from './utils/audience-preview';
import {
  DestinationLookups,
  isCustomAudience,
  isCustomQueryAudience,
  mapActivationDestination,
  mapApiAudience,
  mapAudienceToFormValue
} from './utils/audience-mappers';

export type { AudienceItem } from './utils/audience.types';

const ITALIAN_REGIONS = [
  'Abruzzo',
  'Basilicata',
  'Calabria',
  'Campania',
  'Emilia-Romagna',
  'Friuli-Venezia Giulia',
  'Lazio',
  'Liguria',
  'Lombardy',
  'Marche',
  'Molise',
  'Piedmont',
  'Apulia',
  'Sardinia',
  'Sicily',
  'Trentino-Alto Adige/Südtirol',
  'Tuscany',
  'Umbria',
  'Aosta Valley',
  'Veneto'
] as const;

type AudienceFilterId = AudienceStatus | 'billable';
@Component({
  selector: 'app-audience',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, AudienceCreateComponent, DestinationActivationModalComponent, ActivationEditModalComponent, AudienceExtractionsChartComponent, AudienceAdditionalInfoComponent, AudienceActivationPushChartComponent],
  templateUrl: './audience.html',
  styleUrl: './audience.scss'
})
export class AudienceComponent implements OnInit, OnDestroy {
  audiences: AudienceItem[] = [];
  ruleAudienceOptions: Array<{ id: string; name: string }> = [];
  private _editOwnerEntityId: string | null = null;
  private _editOwnerEntityType: string | null = null;
  hasGlobalDataScope = false;

  get createFormOwnerInfo(): { id: string; name: string } | null {
    if (!this.hasGlobalDataScope) return null;

    if (this.creationMode === 'edit' && this._editOwnerEntityId) {
      const name = this.resolveOwnerLabel(this._editOwnerEntityType, this._editOwnerEntityId);
      return { id: this._editOwnerEntityId, name };
    }

    const TNCDataEntityId = environment.TNCDataEntityId;
    if (!TNCDataEntityId) return null;
    const TNCDataEntity = this.resolveOwnerLabel(null, TNCDataEntityId);
    return { id: TNCDataEntityId, name: TNCDataEntity };
  }

  get ruleAudienceOptionsForForm(): Array<{ id: string; name: string }> {
    if (this.creationMode === 'edit' && this.editingAudienceId) {
      return this.ruleAudienceOptions.filter(o => o.id !== this.editingAudienceId);
    }
    return this.ruleAudienceOptions;
  }
  customImportOptions: Array<{ id: string; name: string }> = [];
  sourceGroupOptions: Array<{ id: string; name: string; type?: string | null }> = [];
  availableDestinations: DestinationDto[] = [];
  activatingDestinationId: number | null = null;
  private loadedGoalOptions: GoalOption[] = [];
  private ownerLabels = new Map<string, string>();
  private sourceGroupMap = new Map<string, GroupDto>();
  isEntityTNCIDPlusMember = false;
  private _audiencesLoaded = false;
  private _customImportsLoaded = false;
  private _providersLoaded = false;

  get optionsReady(): boolean {
    return this._audiencesLoaded && this._customImportsLoaded && this._providersLoaded;
  }
  private providerMap = new Map<string, ProviderDto>();
  countryOptions: CountryOption[] = [];
  private countriesLoaded = false;
  private countriesLoadPromise: Promise<void> | null = null;

  readonly statusColors: Record<AudienceFilterId, string> = {
    active: '#22c55e',
    paused: '#9ca3af',
    pending: '#f97316',
    error: '#ef4444',
    draft: '#2563eb',
    billable: '#a855f7'
  };

  readonly availableStatuses: { id: AudienceFilterId; labelKey: string }[] = [
    { id: 'active', labelKey: 'audiences.listFilters.status.active' },
    { id: 'draft', labelKey: 'audiences.listFilters.status.draft' },
    { id: 'paused', labelKey: 'audiences.listFilters.status.paused' },
    { id: 'pending', labelKey: 'audiences.listFilters.status.pending' },
    { id: 'error', labelKey: 'audiences.listFilters.status.error' },
    { id: 'billable', labelKey: 'audiences.listFilters.status.billable' }
  ];

  searchTerm = sessionStorage.getItem('audience_search_term') ?? '';
  ownerFilterOpen = false;
  ownerFilterPosition = { top: 0, left: 0 };
  ownerFilterSearch = '';
  ownerFilterSortBy: 'name' | 'count' = 'name';
  ownerFilterSortDirection: 'asc' | 'desc' = 'asc';
  selectedOwnerIds = new Set<string>();
  statusFilters: Record<AudienceFilterId, boolean> = this.createStatusFilters();
  sortDirection: 'asc' | 'desc' = 'asc';
  sortColumn: 'name' | 'volume' = 'name';
  activeActionMenuId: string | null = null;
  activeActionAudience: AudienceItem | null = null;
  actionMenuPosition = { top: 0, left: 0 };
  actionMenuArrowOffset = 24;
  actionMenuPlacement: 'below' | 'above' = 'below';

  selectedAudience: AudienceItem | null = null;
  mode: 'list' | 'create' = 'list';
  isLoading = false;
  loadingError: string | null = null;
  creatingAudience = false;
  previewingAudience = false;
  creationError: string | null = null;
  pageSizeOptions = [10, 25, 50];
  pageSize = 10;
  currentPage = 0;
  creationMode: 'create' | 'edit' | 'clone' = 'create';
  editingAudienceId: string | null = null;
  editingAudienceIsPublic: boolean | null = null;
  createInitialValue: Partial<AudienceCreateFormValue> | null = null;
  statusUpdatingId: string | null = null;
  deletingAudienceId: string | null = null;
  exportingAudienceId: string | null = null;
  reportingErrorAudienceId: string | null = null;
  private extractionTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  overlapLoadingId: string | null = null;
  overlapResults: AudienceIntersection[] = [];
  overlapSourceAudienceId: string | null = null;
  overlapModalOpen = false;
  preparingAudienceId: string | null = null;
  obsoleteFiltersInEdit: string[] = [];
  private curvePendingAudienceIds = new Set<string>();
  private curveAttemptedAudienceIds = new Set<string>();
  deleteModalOpen = false;
  deleteTargetAudience: AudienceItem | null = null;
  exportModalOpen = false;
  exportModalTarget: AudienceItem | null = null;
  statusModalOpen = false;
  statusModalTarget: AudienceItem | null = null;
  destinationModalOpen = false;
  destinationModalTarget: AudienceItem | null = null;
  destinationSearch = '';
  overlapSearch = '';
  overlapSortColumn: 'owner' | 'name' | 'tncIds' | 'targetOverlap' = 'tncIds';
  overlapSortDirection: 'asc' | 'desc' = 'desc';
  activationStatusUpdatingId: string | null = null;
  activationEditTarget: ActivationEditTarget | null = null;
  activeDestinationActionMenuId: string | null = null;
  activeDestinationActionMenu: AudienceDestination | null = null;
  destinationActionMenuPosition = { top: 0, left: 0 };
  destinationActionMenuArrowOffset = 24;
  destinationActionMenuPlacement: 'below' | 'above' = 'below';
  forcePushModalOpen = false;
  forcePushModalTarget: AudienceDestination | null = null;
  forcePushingId: string | null = null;

  private brokenLogoIds = new Set<number>();
  private copiedExternalSegmentIds = new Set<string>();

  private subscriptions = new Subscription();
  private destinationNames = new Map<string, string>();
  private destinationTypeInfo = new Map<string, { typeId: number; typeName: string }>();
  private allDestinationsById = new Map<string, DestinationDto>();
  private readonly modeStorageKey = 'audiences.mode';
  private suppressNextPopState = false;
  private scrollListener: ((event: Event) => void) | null = null;
  @ViewChild(AudienceCreateComponent) audienceCreateForm?: AudienceCreateComponent;
  private shouldProtectCreateExit = false;
  private skipCreateExitWarning = false;
  createExitConfirmOpen = false;
  sseForceExitOpen = false;
  sseForceExitReason: 'updated' | 'deleted' | null = null;
  /** IDs audience salvati dalla finestra corrente, per non mostrare il dialog SSE a chi ha fatto la modifica. */
  private recentlySavedAudienceIds = new Set<string>();
  private pendingCreateExitAction: (() => void | Promise<void>) | null = null;
  private pendingCreateExitResolver: ((confirmed: boolean) => void) | null = null;
  private pendingCreateExitOptions: NavigationBlockOptions | null = null;
  private readonly createExitNavigationBlocker = (
    action?: () => void | Promise<void>,
    options?: NavigationBlockOptions
  ) => this.confirmCreateExitIfNeeded(action, options);

  get canCreateAudience(): boolean {
    const creationDcrId = this.authService.getAudienceCreationDcrId();
    return this.authService.hasPermission(UserPermissions.REACH_AUDIENCE) && !!creationDcrId;
  }

  /**
   * Advanced audience insights (global extractions chart, per-audience volumes/push charts and the
   * "Additional Info" tabs). Authorization floor stays `admin_dashboard.all`, AND they're surfaced
   * only when the `audienceInsights` experimental flag is on (opt-in beta, not an authz mechanism).
   */
  get showAdvancedInsights(): boolean {
    return this.authService.hasPermission(UserPermissions.ADMIN_DASHBOARD)
      && this.experimentalFeatures.isEnabled('audienceInsights');
  }

  get canManageAudienceActions(): boolean {
    return this.canCreateAudience;
  }

  get hasGlobalDataAccess(): boolean {
    return this.authService.hasGlobalDataAccess();
  }

  get isTNCDataEntityForCreation(): boolean {
    const dcrId = this.authService.getAudienceCreationDcrId();
    return dcrId === environment.TNCDataEntityId;
  }

  get isCurrentEntityDataProvider(): boolean {
    return this.entityConfigService.isCurrentEntityDataProvider;
  }

  get canMakePublicAudience(): boolean {
    return this.isTNCDataEntityForCreation || this.isCurrentEntityDataProvider;
  }

  get shouldSkipDcrRuleFilters(): boolean {
    return this.isTNCDataEntityForCreation;
  }

  get selectedEntityName(): string {
    return this.resolveSelectedEntityName();
  }

  hasIncompatibleFilters(audience: AudienceItem): boolean {
    if (isCustomQueryAudience(audience)) return false;
    return this.inferHasIncompatibleFilters(audience.filterItems ?? [], audience.queryComposition);
  }

  getObsoleteFilterDescriptions(audience: AudienceItem): string[] {
    if (isCustomQueryAudience(audience)) return [];
    const interestTypes = [FILTER_TYPES.SOCIODEMO, FILTER_TYPES.GEO, FILTER_TYPES.TIMERANGE] as string[];
    const filterItems = audience.filterItems ?? [];
    const result: string[] = [];

    if (!filterItems.length) {
      if (!audience.queryComposition?.trim()) return [];
      const tokens = audience.queryComposition.match(/([A-Za-z]+)#\d+/g) ?? [];
      const types = [...new Set(tokens.map(t => t.split('#')[0]))];
      types.filter(t => !mapFilterTypeToRuleGroup(t) && !interestTypes.includes(t)).forEach(t => result.push(t));
      const hasRules = types.some(t => !!mapFilterTypeToRuleGroup(t));
      const hasInterests = types.some(t => interestTypes.includes(t));
      if (hasRules && hasInterests) result.push('__mixed__');
      return [...new Set(result)];
    }

    const hasRuleItems = filterItems.some(item => !!mapFilterTypeToRuleGroup(item.filterType));
    const hasInterestItems = filterItems.some(item => interestTypes.includes(item.filterType ?? ''));
    filterItems.forEach(item => {
      const type = item.filterType ?? '';
      if (!mapFilterTypeToRuleGroup(type) && !interestTypes.includes(type)) result.push(type);
    });
    if (hasRuleItems && hasInterestItems) result.push('__mixed__');
    return [...new Set(result)];
  }

  get obsoleteFiltersDisplayLabel(): string {
    return this.obsoleteFiltersInEdit
      .map(t => t === '__mixed__' ? this.translate.instant('audiences.filterTypes.__mixed__') : t)
      .join(', ');
  }

  canEditAudience(audience: AudienceItem): boolean {
    // hasGlobalDataScope() implica già hasGlobalDataAccess (è quello + nessuna entity selezionata),
    // quindi la doppia condizione era ridondante.
    if (this.authService.hasGlobalDataScope()) {
      // Nessuna entity specifica selezionata ("Global Data Scope"): può editare tutte le audience
      return true;
    }

    if (this.hasGlobalDataAccess && this.isTNCDataEntityForCreation) {
      // "_TNC Data" explicitly selected: only edit audiences owned by admin entity
      return this.normalizeEntityId(audience.entityId) === this.normalizeEntityId(environment.TNCDataEntityId);
    }

    // Non-admin users, or admins acting as a specific entity, can only edit
    // audiences owned by the currently selected entity
    const selectedDcrId = this.getSelectedDcrFilter();
    if (!selectedDcrId) {
      return false;
    }

    return this.normalizeEntityId(audience.entityId) === selectedDcrId;
  }

  canManageAudienceDefinition(audience: AudienceItem): boolean {
    return this.canEditAudience(audience);
  }

  isAudienceQuickActionsDisabled(audience: AudienceItem): boolean {
    // Quick actions are disabled when audience is running (isRunning === true)
    return !!audience.isRunning;
  }

  shouldShowCurveWarning(audience: AudienceItem | null | undefined): boolean {
    if (!audience) {
      return false;
    }

    if (audience.status === 'draft' && !audience.previewAccuracy) {
      return true;
    }

    return this.shouldAttemptCurveDataLoad(audience) && !audience.previewAccuracy;
  }

  shouldShowUnmanageableAudienceBadge(audience: AudienceItem): boolean {
    return this.hasIncompatibleFilters(audience);
  }

  shouldShowAudienceStatusSpinner(audience: AudienceItem): boolean {
    return this.shouldShowCurveWarning(audience) && this.curvePendingAudienceIds.has(audience.id);
  }

  isAudienceVolumeCalculationPending(audience: AudienceItem | null | undefined): boolean {
    if (!audience) {
      return false;
    }
    return this.shouldShowCurveWarning(audience);
  }

  canExportAudience(audience: AudienceItem): boolean {
    if (this.hasGlobalDataAccess) {
      return true;
    }

    // Non-admin users can export:
    // 1. Their own audiences (from their DCR)
    // 2. Public audiences (each user exports to their own destinations)
    const selectedDcrId = this.getSelectedDcrFilter();
    if (!selectedDcrId) {
      return false;
    }

    const normalizedEntityId = this.normalizeEntityId(audience.entityId);
    const isOwnAudience = normalizedEntityId === selectedDcrId;
    const isPublic = audience.isPublic === true;

    return Boolean(+isOwnAudience ^ +isPublic); // XOR: solo una delle due condizioni può essere vera
  }

  hasAnyActions(audience: AudienceItem): boolean {
    if (audience.status === 'error') return true;
    return this.canEditAudience(audience) || this.canManageAudienceActions;
  }

  constructor(
    private translate: TranslateService,
    private audienceApi: AudienceApiService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private notificationBackendService: NotificationBackendService,
    private customImportApi: CustomImportApiService,
    private groupsApi: GroupsApiService,
    private destinationsApi: DestinationsApiService,
    private entityOwnerService: EntityOwnerService,
    private providersApi: ProvidersApiService,
    private entityConfigService: EntityConfigService,
    private navigationBlockService: NavigationBlockService,
    private creationStateService: CreationStateService,
    private experimentalFeatures: ExperimentalFeaturesService
  ) {
    this.subscriptions.add(
      this.entityOwnerService.owners$.subscribe(map => {
        this.ownerLabels = map;
      })
    );
    // Reload when the active DCR changes OR when the impersonated user changes identity.
    // debounceTime(0) coalesces the two synchronous emissions from startImpersonation
    // (selectedDcrSubject + currentUserSubject) into a single handleActiveDcrChange call.
    this.subscriptions.add(
      merge(
        this.authService.selectedDcr$.pipe(distinctUntilChanged(), skip(1)),
        this.authService.currentUser$.pipe(map(u => u?.ID ?? null), distinctUntilChanged(), skip(1))
      )
        .pipe(debounceTime(0))
        .subscribe(() => this.handleActiveDcrChange())
    );
  }

  ngOnInit(): void {
    this.initializeModeState();

    // Track admin mode reactively so createFormOwnerInfo is always in sync
    this.hasGlobalDataScope = this.authService.hasGlobalDataScope();
    this.subscriptions.add(
      this.authService.selectedDcr$.subscribe(() => {
        this.hasGlobalDataScope = this.authService.hasGlobalDataScope();
      })
    );

    const pendingSelectId = typeof window !== 'undefined'
      ? window.sessionStorage.getItem('audience_pending_select_id')
      : null;
    if (pendingSelectId) {
      window.sessionStorage.removeItem('audience_pending_select_id');
    }
    this.fetchAudiences(pendingSelectId ?? undefined);
    this.loadCustomImports();
    this.loadSourceGroups();
    this.loadProviders();
    this.loadDestinations();

    // Subscribe to audience extraction completion notifications
    this.subscriptions.add(
      this.notificationBackendService.audienceExtractionCompleted$.subscribe(({ audienceId, audience }) => {
        const handle = this.extractionTimeouts.get(audienceId);
        if (handle !== undefined) {
          clearTimeout(handle);
          this.extractionTimeouts.delete(audienceId);
        }
        // Successo con snapshot (volumi già su MySQL, vedi extract-audience.ts): aggiorniamo solo
        // la riga. Errore, snapshot assente o audience non in lista: ricarichiamo la lista
        // mantenendo lo stato corrente (filtri, selezione, paginazione).
        if (audience && this.audiences.some(a => a.id === audienceId)) {
          this.updateAudience(audienceId, this.mapExtractionSnapshot(audience));
          return;
        }
        this.fetchAudiences(this.selectedAudience?.id, true);
      })
    );

    // Real-time audience created/updated: upsert in list
    this.subscriptions.add(
      this.notificationBackendService.audienceUpserted$.subscribe(async audienceId => {
        // Se siamo stati noi a salvare in questa finestra, ignoriamo il dialog ma aggiorniamo comunque la lista
        const isSelf = this.recentlySavedAudienceIds.has(audienceId);
        if (isSelf) this.recentlySavedAudienceIds.delete(audienceId);

        const fetched = await this.fetchAudienceById(audienceId);
        if (!fetched) return;
        const exists = this.audiences.some(a => a.id === audienceId);
        if (exists) {
          this.updateAudience(audienceId, fetched);
          if (this.selectedAudience?.id === audienceId) {
            this.selectedAudience = fetched;
          }
          // Mostra il dialog solo se la modifica è arrivata da un'altra finestra/utente
          if (!isSelf && this.mode === 'create' && this.creationMode === 'edit' && this.editingAudienceId === audienceId) {
            this.sseForceExitReason = 'updated';
            this.sseForceExitOpen = true;
          }
        } else {
          this.audiences = [fetched, ...this.audiences];
        }
        // The status may have just become (or stopped being) 'active': keep the pickable list in sync.
        this.rebuildRuleAudienceOptions();
      })
    );

    // Real-time audience deleted: remove from list
    this.subscriptions.add(
      this.notificationBackendService.audienceDeleted$.subscribe(audienceId => {
        this.audiences = this.audiences.filter(a => a.id !== audienceId);
        this.rebuildRuleAudienceOptions();
        if (this.selectedAudience?.id === audienceId) {
          this.selectedAudience = null;
        }
        // Warn user if they are editing this audience
        if (this.mode === 'create' && this.creationMode === 'edit' && this.editingAudienceId === audienceId) {
          this.sseForceExitReason = 'deleted';
          this.sseForceExitOpen = true;
        }
      })
    );

    // Add scroll listener to close menu/panel on scroll, but ignore scroll
    // events originating from inside the menu/panel itself (e.g. scrolling
    // a long owner list), since 'scroll' only reaches document via capture.
    this.scrollListener = (event: Event) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (this.activeActionMenuId && !target?.closest('.audience-action-menu')) {
        this.closeActionMenu();
      }
      if (this.activeDestinationActionMenuId && !target?.closest('.destination-action-menu')) {
        this.closeDestinationActionMenu();
      }
      if (this.ownerFilterOpen && !target?.closest('.filter-dropdown')) {
        this.closeOwnerFilterPanel();
      }
    };
    document.addEventListener('scroll', this.scrollListener, true);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.extractionTimeouts.forEach(handle => clearTimeout(handle));
    this.extractionTimeouts.clear();
    if (this.scrollListener) {
      document.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
    this.navigationBlockService.clear(this.createExitNavigationBlocker);
    // Ensure entity selector is always re-enabled when component is destroyed
    this.creationStateService.setCreationMode(false);
  }

  selectAudience(audience: AudienceItem): void {
    this.selectedAudience = audience;
    this.showStoppedDestinations = false;
    if (this.isAudienceQuickActionsDisabled(audience)) {
      this.closeActionMenu();
    }
    // Curve data is now loaded only when entering edit/clone/approve
    if (this.overlapSourceAudienceId !== audience.id) {
      this.overlapResults = [];
      if (this.overlapLoadingId && this.overlapLoadingId !== audience.id) {
        this.overlapLoadingId = null;
      }
      this.overlapSourceAudienceId = null;
      this.overlapModalOpen = false;
    }
  }

  get destinationModalOptions(): DestinationDto[] {
    return this.filterDestinations(
      this.getAvailableDestinations(this.destinationModalTarget),
      this.destinationSearch
    );
  }

  get filteredOverlapResults(): AudienceIntersection[] {
    const filtered = this.filterOverlapResults(this.overlapResults, this.overlapSearch);
    return this.applyOverlapSort(filtered);
  }

  setOverlapSort(column: 'owner' | 'name' | 'tncIds' | 'targetOverlap'): void {
    if (this.overlapSortColumn === column) {
      this.overlapSortDirection = this.overlapSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.overlapSortColumn = column;
      // Text columns read best ascending; numeric overlaps most-relevant-first (desc).
      this.overlapSortDirection = column === 'name' || column === 'owner' ? 'asc' : 'desc';
    }
  }

  private applyOverlapSort(results: AudienceIntersection[]): AudienceIntersection[] {
    const dir = this.overlapSortDirection === 'asc' ? 1 : -1;
    return [...results].sort((a, b) => {
      let comparison = 0;
      switch (this.overlapSortColumn) {
        case 'owner':
          comparison = (a.targetOwnerLabel || '').toLowerCase()
            .localeCompare((b.targetOwnerLabel || '').toLowerCase());
          break;
        case 'name':
          comparison = (a.targetAudienceName || '').toLowerCase()
            .localeCompare((b.targetAudienceName || '').toLowerCase());
          break;
        case 'tncIds':
          comparison = a.tncIds - b.tncIds;
          break;
        case 'targetOverlap':
          comparison = (a.targetOverlap || 0) - (b.targetOverlap || 0);
          break;
      }
      return dir * comparison;
    });
  }

  showStoppedDestinations = false;

  get activeDestinations(): AudienceDestination[] {
    return (this.selectedAudience?.destinations || []).filter(dest => (dest.status || '').toLowerCase() === 'active');
  }

  get stoppedDestinations(): AudienceDestination[] {
    return (this.selectedAudience?.destinations || []).filter(dest => {
      const status = (dest.status || '').toLowerCase();
      return status === 'stopped' || status === 'ended';
    });
  }

  get activeDestinationsByType(): Array<{ typeId: number | null; typeName: string; destinations: AudienceDestination[] }> {
    return this.groupDestinationsByType(this.activeDestinations);
  }

  get stoppedDestinationsByType(): Array<{ typeId: number | null; typeName: string; destinations: AudienceDestination[] }> {
    return this.groupDestinationsByType(this.stoppedDestinations);
  }

  getDestinationTypeIconPath(typeId: number | null): string | null {
    if (typeId == null || this.brokenLogoIds.has(typeId)) return null;
    return getDestinationLogoPath(typeId);
  }

  onDestinationLogoError(typeId: number | null): void {
    if (typeId != null) this.brokenLogoIds.add(typeId);
  }

  copyExternalSegmentId(destination: AudienceDestination): void {
    if (!destination.externalSegmentId) return;
    const text = destination.externalSegmentId;
    const copyPromise = (typeof navigator !== 'undefined' && navigator.clipboard?.writeText)
      ? navigator.clipboard.writeText(text)
      : this.fallbackCopyText(text);
    copyPromise.then(() => {
      this.copiedExternalSegmentIds.add(destination.id);
      this.notificationService.notify('success', this.translate.instant('audiences.destinations.externalIdCopied'));
      setTimeout(() => { this.copiedExternalSegmentIds.delete(destination.id); }, 2000);
    }).catch(() => {
      this.notificationService.notify('error', this.translate.instant('audiences.details.copyError'));
    });
  }

  isExternalSegmentIdCopied(destination: AudienceDestination): boolean {
    return this.copiedExternalSegmentIds.has(destination.id);
  }

  private groupDestinationsByType(destinations: AudienceDestination[]): Array<{ typeId: number | null; typeName: string; destinations: AudienceDestination[] }> {
    const groupMap = new Map<string, { typeId: number | null; typeName: string; destinations: AudienceDestination[] }>();
    for (const dest of destinations) {
      const key = dest.destinationTypeId != null ? String(dest.destinationTypeId) : '__unknown__';
      if (!groupMap.has(key)) {
        groupMap.set(key, { typeId: dest.destinationTypeId, typeName: dest.destinationTypeName || '—', destinations: [] });
      }
      groupMap.get(key)!.destinations.push(dest);
    }
    const groups = Array.from(groupMap.values());
    groups.sort((a, b) => a.typeName.localeCompare(b.typeName));
    groups.forEach(g => g.destinations.sort((a, b) => a.name.localeCompare(b.name)));
    return groups;
  }

  trackDestinationGroup(_index: number, group: { typeId: number | null }): string {
    return group.typeId != null ? String(group.typeId) : '__unknown__';
  }

  trackDestination(_index: number, destination: AudienceDestination): string {
    return destination.id;
  }

  getDetailPreviewYAxisTicks(): Array<{ y: number; value: string }> {
    const points = this.selectedAudience?.previewAccuracy?.points || [];
    if (!points.length) {
      return [];
    }
    const maxUsers = Math.max(...points.map(point => point.users || 0), 1);

    const tickCount = 5;
    return Array.from({ length: tickCount + 1 }, (_, i) => {
      const ratio = i / tickCount;
      const value = Math.round(maxUsers * ratio);
      return {
        y: 180 - (ratio * 160),
        value: this.formatCompactNumber(value)
      };
    });
  }

  getDetailPreviewChartPath(): string {
    const points = this.selectedAudience?.previewAccuracy?.points || [];
    if (points.length < 2) {
      return '';
    }
    const width = 520;
    const height = 220;
    const left = 40;
    const bottom = 180;
    const chartWidth = width - left - 24;
    const chartHeight = bottom - 20;
    const maxUsers = Math.max(...points.map(point => point.users || 0), 1);

    let path = '';
    points.forEach((point, index) => {
      const x = left + ((chartWidth / Math.max(points.length - 1, 1)) * index);
      const y = bottom - (((point.users || 0) / maxUsers) * chartHeight);
      path += (index === 0 ? 'M' : ' L') + ` ${x.toFixed(2)} ${y.toFixed(2)}`;
    });

    return path;
  }

  getDetailPreviewChartAreaPath(): string {
    const points = this.selectedAudience?.previewAccuracy?.points || [];
    if (points.length < 2) {
      return '';
    }
    const width = 520;
    const height = 220;
    const left = 40;
    const bottom = 180;
    const chartWidth = width - left - 24;
    const chartHeight = bottom - 20;
    const maxUsers = Math.max(...points.map(point => point.users || 0), 1);

    const firstX = left;
    let path = `M ${firstX} ${bottom}`;

    points.forEach((point, index) => {
      const x = left + ((chartWidth / Math.max(points.length - 1, 1)) * index);
      const y = bottom - (((point.users || 0) / maxUsers) * chartHeight);
      path += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
    });

    const lastX = left + chartWidth;
    path += ` L ${lastX} ${bottom} Z`;

    return path;
  }

  private formatCompactNumber(value: number): string {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`;
    }
    return `${value}`;
  }

  formatNumberWithSeparator(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
  }

  getOverlapPercent(value?: number | null): number {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 0;
    }
    const percent = value * 100;
    return Math.max(0, Math.min(100, percent));
  }

  activateDestination(destination: DestinationDto, audience?: AudienceItem | null): void {
    if (!this.canManageAudienceActions) {
      return;
    }
    const targetAudience = audience ?? this.selectedAudience;
    if (!targetAudience) {
      return;
    }
    const channelData = this.buildActivationChannelData(destination, targetAudience);
    if (!channelData) {
      this.notificationService.notify('error', this.translate.instant('audiences.destinations.activationUnavailable'));
      return;
    }
    this.activatingDestinationId = destination.ID;
    const sub = this.audienceApi.addActivationItem({
      AudienceID: targetAudience.id,
      DestinationID: destination.ID,
      ChannelData: channelData,
      NextExecutionDate: todayIsoDate()
    }).subscribe({
      next: () => {
        this.notificationService.notify('success', this.translate.instant('audiences.destinations.activationSuccess', { name: destination.Name }));
        this.activatingDestinationId = null;
        const keepSelectionId = this.selectedAudience?.id ?? targetAudience.id;
        this.fetchAudiences(keepSelectionId, true);
      },
      error: (error) => {
        console.error('[audience] destination activation failed', error);
        this.activatingDestinationId = null;
        const message = error?.message || this.translate.instant('audiences.destinations.activationError');
        this.notificationService.notify('error', message);
      }
    });
    this.subscriptions.add(sub);
  }

  toggleDestinationStatus(destination: AudienceDestination): void {
    if (!destination?.id || this.activationStatusUpdatingId) {
      return;
    }
    this.closeDestinationActionMenu();
    const currentStatus = (destination.status || '').toLowerCase();
    const nextStatus = currentStatus === 'active' ? 'stopped' : 'active';
    this.activationStatusUpdatingId = destination.id;
    const sub = this.audienceApi.updateActivationStatus({
      AudienceActivationID: destination.id,
      status: nextStatus
    }).subscribe({
      next: () => {
        this.activationStatusUpdatingId = null;
        if (this.selectedAudience?.id) {
          this.fetchAudiences(this.selectedAudience.id, true);
        }
      },
      error: (error) => {
        console.error('[audience] failed to update activation status', error);
        this.activationStatusUpdatingId = null;
      }
    });
    this.subscriptions.add(sub);
  }

  /**
   * A user can edit the activation params only if the destination is visible
   * to them (i.e. it appears in their webapp/crud/destinations list).
   */
  hasDestinationAccess(destination: AudienceDestination): boolean {
    return !!destination?.destinationId && this.destinationNames.has(destination.destinationId);
  }

  getDestinationOwnerLabel(destination: AudienceDestination): string {
    const record = destination.destinationId ? this.allDestinationsById.get(destination.destinationId) : null;
    const entityId = record?.EntityID ?? destination.entityIdRaw ?? null;
    const entityType = record?.EntityType ?? null;
    return this.resolveOwnerLabel(entityType, entityId);
  }

  openActivationEdit(destination: AudienceDestination, audience?: AudienceItem | null): void {
    const targetAudience = audience ?? this.selectedAudience;
    if (!this.hasDestinationAccess(destination) || !destination?.destinationId || !targetAudience) {
      return;
    }
    this.closeDestinationActionMenu();
    const channelSegmentId = destination.channelData?.['ExternalSegmentID'];
    const externalSegmentId = destination.externalSegmentIdRaw
      || (channelSegmentId != null && channelSegmentId !== '' ? String(channelSegmentId) : null)
      || targetAudience.id;
    this.activationEditTarget = {
      activationId: destination.id,
      audienceId: targetAudience.id,
      destinationId: Number(destination.destinationId),
      destinationTypeId: destination.destinationTypeId,
      destinationName: destination.name,
      externalSegmentId,
      channelData: destination.channelData,
      nextExecutionDate: destination.nextExecutionRaw ?? null,
      excludeControlGroup: destination.excludeControlGroup
    };
  }

  closeActivationEdit(): void {
    this.activationEditTarget = null;
  }

  handleActivationEdited(): void {
    this.activationEditTarget = null;
    if (this.selectedAudience?.id) {
      this.fetchAudiences(this.selectedAudience.id, true);
    }
  }

  toggleDestinationActionsMenu(destination: AudienceDestination, event?: Event): void {
    if (!destination?.id) {
      return;
    }
    event?.stopPropagation();
    const isOpen = this.activeDestinationActionMenuId === destination.id;
    if (isOpen) {
      this.closeDestinationActionMenu();
      return;
    }

    this.activeDestinationActionMenuId = destination.id;
    this.activeDestinationActionMenu = destination;
    const trigger = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (trigger) {
      // The menu has a small, bounded set of items, so a single estimate-based
      // placement is used instead of a delayed re-measurement: the destination
      // list is rebuilt from scratch on every change-detection cycle (see
      // groupDestinationsByType), so a captured trigger reference can go stale
      // by the time a follow-up measurement would run.
      this.updateDestinationActionMenuPosition(trigger);
    } else {
      this.destinationActionMenuPosition = { top: 0, left: 0 };
      this.destinationActionMenuArrowOffset = 24;
    }
  }

  private closeDestinationActionMenu(): void {
    this.activeDestinationActionMenuId = null;
    this.activeDestinationActionMenu = null;
    this.destinationActionMenuArrowOffset = 24;
  }

  confirmForcePush(destination: AudienceDestination, event?: Event): void {
    if (!this.hasGlobalDataAccess || !destination?.id) {
      return;
    }
    event?.stopPropagation();
    this.forcePushModalTarget = destination;
    this.forcePushModalOpen = true;
    this.closeDestinationActionMenu();
  }

  closeForcePushModal(): void {
    if (this.forcePushingId) {
      return;
    }
    this.forcePushModalOpen = false;
    this.forcePushModalTarget = null;
  }

  performForcePush(): void {
    if (!this.hasGlobalDataAccess) {
      this.closeForcePushModal();
      return;
    }
    const destination = this.forcePushModalTarget;
    if (!destination?.id) {
      this.forcePushModalOpen = false;
      return;
    }
    this.forcePushModalOpen = false;
    this.forcePushingId = destination.id;
    const audienceName = this.selectedAudience?.name || '';
    const sub = this.audienceApi.pushActivation({
      AudienceActivationID: destination.id,
      AudienceName: audienceName
    }).subscribe({
      next: () => {
        this.forcePushingId = null;
        this.forcePushModalTarget = null;
        this.notificationService.notify('success', this.translate.instant('audiences.destinations.forcePushSuccess', { name: audienceName }));
      },
      error: (error) => {
        console.error('[audience] failed to force push activation', error);
        this.forcePushingId = null;
        this.forcePushModalTarget = null;
        this.notificationService.notify('error', this.resolveActionErrorMessage(error, 'audiences.destinations.forcePushError'));
      }
    });
    this.subscriptions.add(sub);
  }

  startCreate(): void {
    if (!this.canCreateAudience) {
      return;
    }
    this.loadCountries();
    this.activateCreateView({
      creationMode: 'create',
      initialValue: null,
      editingId: null,
      pushHistory: true
    });
  }

  onStaleItemsDetected(): void {
    this.notificationService.notify('warning', this.translate.instant('audiences.actions.staleItemsWarning'));
  }

  onCreateCanceled(): void {
    this.creationError = null;
    this.obsoleteFiltersInEdit = [];
    this.skipCreateExitWarning = true;
    this.navigateBackToList();
  }

  async onAudienceSaved(formValue: AudienceCreateFormValue): Promise<void> {
    if (!this.canCreateAudience) {
      return;
    }
    this.creationError = null;
    this.creatingAudience = true;
    try {
      const description = formValue.aiDescription?.toString().trim() ?? '';
      const suggestedTags = description && !formValue.confirmedWithoutTags
        ? await firstValueFrom(this.generateTags(description))
        : null;
      if (this.creationMode === 'edit' && this.editingAudienceId) {
        const existingAudience = this.audiences.find(a => a.id === this.editingAudienceId);
        const updatePayload = await this.buildUpdatePayload(formValue, suggestedTags, { previewOnly: false });
        if (!updatePayload) {
          throw new Error(this.translate.instant('audiences.create.errorMissingPayload', {
            value: 'Missing audience data'
          }));
        }
        this.recentlySavedAudienceIds.add(this.editingAudienceId);
        const updated = await firstValueFrom(this.audienceApi.updateAudience(updatePayload));

        // Ricarica l'audience aggiornata dal server per ottenere tutti i campi calcolati
        const mapped = await this.fetchAudienceById(updated.ID) ?? this.mapApiAudience(updated);

        this.updateAudience(mapped.id, mapped);
        this.selectedAudience = mapped;
      } else {
        const payload = await this.buildAudiencePayload(formValue, null, suggestedTags, { previewOnly: false });
        if (!payload) {
          throw new Error(this.translate.instant('audiences.create.errorMissingPayload', {
            value: 'Missing audience data'
          }));
        }
        const created = await firstValueFrom(this.audienceApi.createAudience(payload));

        const mapped = await this.fetchAudienceById(created.ID) ?? this.mapApiAudience(created);

        if (this.audiences.some(a => a.id === mapped.id)) {
          this.updateAudience(mapped.id, mapped);
        } else {
          this.audiences = [mapped, ...this.audiences];
          this.rebuildRuleAudienceOptions();
        }
        this.selectedAudience = mapped;
        if (this.shouldAttemptCurveDataLoad(mapped)) {
          this.resetCurveLoadState(mapped.id);
          void this.loadCurveDataForAudience(mapped);
        }
      }
      this.skipCreateExitWarning = true;
      this.navigateBackToList();
      this.notificationService.notify('success', this.translate.instant('audiences.create.success'));
    } catch (error) {
      console.error('[audience] save failed', error);
      const message = error instanceof Error
        ? (error.message || this.translate.instant('audiences.create.errorGeneric', { value: 'Unable to save audience' }))
        : this.translate.instant('audiences.create.errorGeneric', { value: 'Unable to save audience' });
      this.creationError = message;
      this.notificationService.notify('error', message);
    } finally {
      this.creatingAudience = false;
    }
  }

  async onAudiencePreviewRequested(formValue: AudienceCreateFormValue): Promise<void> {
    if (!this.canCreateAudience) {
      return;
    }

    this.creationError = null;
    this.previewingAudience = true;
    try {
      const description = formValue.aiDescription?.toString().trim() ?? '';
      const suggestedTags = description && !formValue.confirmedWithoutTags
        ? await firstValueFrom(this.generateTags(description))
        : null;

      let previewed: AudienceApiResponse;

      if (this.creationMode === 'edit' && this.editingAudienceId) {
        const previewPayload = await this.buildUpdatePayload(formValue, suggestedTags, { forceDraft: true });
        if (!previewPayload) {
          throw new Error(this.translate.instant('audiences.create.errorMissingPayload', {
            value: 'Missing audience data'
          }));
        }
        previewed = await firstValueFrom(this.audienceApi.updateAudience(previewPayload));
      } else {
        const previewPayload = await this.buildAudiencePayload(formValue, null, suggestedTags, { forceDraft: true });
        if (!previewPayload) {
          throw new Error(this.translate.instant('audiences.create.errorMissingPayload', {
            value: 'Missing audience data'
          }));
        }
        previewed = await firstValueFrom(this.audienceApi.createAudience(previewPayload));
      }

      // Mappiamo la risposta IGNORANDO i dati di preview temporanei
      // Vogliamo aspettare che il backend calcoli i dati reali
      const mapped = this.mapApiAudience(previewed, { ignorePreviewData: true });

      if (this.creationMode === 'edit' && this.editingAudienceId) {
        this.updateAudience(mapped.id, mapped);
      } else if (!this.audiences.some(audience => audience.id === mapped.id)) {
        this.audiences = [mapped, ...this.audiences];
        this.rebuildRuleAudienceOptions();
      }

      this.selectedAudience = mapped;

      // Torna alla lista dopo aver salvato la draft
      this.skipCreateExitWarning = true;
      this.navigateBackToList();

      this.resetCurveLoadState(mapped.id);
      void this.loadCurveDataForAudience(mapped);

      this.notificationService.notify('success', this.translate.instant('audiences.create.previewSuccess'));
    } catch (error) {
      console.error('[audience] preview failed', error);
      const message = error instanceof Error
        ? (error.message || this.translate.instant('audiences.create.previewError', { value: 'Unable to preview audience' }))
        : this.translate.instant('audiences.create.previewError', { value: 'Unable to preview audience' });
      this.creationError = message;
      this.notificationService.notify('error', message);
    } finally {
      this.previewingAudience = false;
    }
  }

  onSearch(term: string): void {
    this.searchTerm = term ?? '';
    sessionStorage.setItem('audience_search_term', this.searchTerm);
    this.resetToFirstPage();
  }

  toggleOwnerFilterPanel(event?: Event): void {
    event?.stopPropagation();
    if (this.ownerFilterOpen) {
      this.closeOwnerFilterPanel();
      return;
    }
    this.ownerFilterOpen = true;
    if (event?.currentTarget instanceof HTMLElement) {
      this.updateOwnerFilterPosition(event.currentTarget);
    }
  }

  toggleOwnerFilterOption(ownerId: string): void {
    const next = new Set(this.selectedOwnerIds);
    if (next.has(ownerId)) {
      next.delete(ownerId);
    } else {
      next.add(ownerId);
    }
    this.selectedOwnerIds = next;
    this.resetToFirstPage();
  }

  clearOwnerFilter(event?: Event): void {
    event?.stopPropagation();
    this.selectedOwnerIds = new Set();
    this.ownerFilterSearch = '';
    this.resetToFirstPage();
  }

  private ownerFilterKey(audience: AudienceItem): string {
    return this.normalizeEntityId(audience.entityId) || '__unknown__';
  }

  get ownerFilterOptions(): Array<{ id: string; label: string; count: number }> {
    const normalizedSearch = this.searchTerm.toLowerCase().trim();
    const counts = new Map<string, { label: string; count: number }>();
    for (const audience of this.audiences) {
      if (!this.audiencePassesStatusFilters(audience)) continue;
      if (!this.matchesSearch(audience, normalizedSearch)) continue;
      const key = this.ownerFilterKey(audience);
      const entry = counts.get(key);
      if (entry) {
        entry.count += 1;
      } else {
        counts.set(key, { label: this.getAudienceOwner(audience), count: 1 });
      }
    }
    const options = [...counts.entries()].map(([id, { label, count }]) => ({ id, label, count }));
    const dir = this.ownerFilterSortDirection === 'asc' ? 1 : -1;
    if (this.ownerFilterSortBy === 'count') {
      return options.sort((a, b) => dir * (a.count - b.count) || a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
    }
    return options.sort((a, b) => dir * a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  }

  setOwnerFilterSort(sortBy: 'name' | 'count'): void {
    if (this.ownerFilterSortBy === sortBy) {
      this.ownerFilterSortDirection = this.ownerFilterSortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }
    this.ownerFilterSortBy = sortBy;
    this.ownerFilterSortDirection = sortBy === 'count' ? 'desc' : 'asc';
  }

  get filteredOwnerFilterOptions(): Array<{ id: string; label: string; count: number }> {
    const term = this.ownerFilterSearch.toLowerCase().trim();
    if (!term) {
      return this.ownerFilterOptions;
    }
    return this.ownerFilterOptions.filter(option => option.label.toLowerCase().includes(term));
  }

  get ownerFilterButtonLabel(): string {
    if (this.selectedOwnerIds.size === 0) {
      return this.translate.instant('audiences.listFilters.owner.buttonDefault');
    }
    const selectedLabels = this.ownerFilterOptions
      .filter(option => this.selectedOwnerIds.has(option.id))
      .map(option => option.label);
    if (!selectedLabels.length) {
      return this.translate.instant('audiences.listFilters.owner.buttonDefault');
    }
    if (selectedLabels.length === 1) {
      return selectedLabels[0];
    }
    return `${selectedLabels[0]} +${selectedLabels.length - 1}`;
  }

  get isOwnerFilterEnabled(): boolean {
    return this.experimentalFeatures.isEnabled('audienceOwnerFilter');
  }

  private audiencePassesOwnerFilter(audience: AudienceItem): boolean {
    if (!this.isOwnerFilterEnabled || this.selectedOwnerIds.size === 0) {
      return true;
    }
    return this.selectedOwnerIds.has(this.ownerFilterKey(audience));
  }

  toggleStatusFilter(status: AudienceFilterId): void {
    this.statusFilters = {
      ...this.statusFilters,
      [status]: !this.statusFilters[status]
    };
    this.resetToFirstPage();
  }

  resetFilters(): void {
    this.statusFilters = this.createStatusFilters();
    this.searchTerm = '';
    this.selectedOwnerIds = new Set();
    this.ownerFilterSearch = '';
    sessionStorage.removeItem('audience_search_term');
    this.resetToFirstPage();
  }

  get filteredAudiences(): AudienceItem[] {
    return this.computeFilteredAudiences();
  }

  toggleNameSort(): void {
    if (this.sortColumn === 'name') {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = 'name';
      this.sortDirection = 'asc';
    }
    this.resetToFirstPage();
  }

  toggleVolumeSort(): void {
    if (this.sortColumn === 'volume') {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = 'volume';
      this.sortDirection = 'asc';
    }
    this.resetToFirstPage();
  }

  /**
   * Whether the entity is the global TNC-owned data entity (`environment.TNCDataEntityId`).
   * Matched by ID only, on purpose: the display name is owned by the DB and can be
   * renamed, so comparing against it would silently stop matching.
   */
  private isTncOwnedEntity(entityId?: string | null): boolean {
    const normalized = this.normalizeEntityId(entityId);
    return !!normalized && normalized === this.normalizeEntityId(environment.TNCDataEntityId);
  }

  getAudienceOwner(audience: AudienceItem): string {
    if (this.isTncOwnedEntity(audience.entityId)) {
      return 'TNC';
    }
    return this.resolveOwnerLabel(audience.entityType, audience.entityId);
  }

  /**
   * Label for the badge on TNC audiences. Same rule as the owner label: TNC-owned
   * audiences read "TNC", while public audiences owned by someone else keep their
   * owner name. Kept as a named alias so the template call site states its intent.
   */
  getTncBadgeLabel(audience: AudienceItem): string {
    return this.getAudienceOwner(audience);
  }

  isTncAudience(audience: AudienceItem): boolean {
    return this.isTncOwnedEntity(audience.entityId) || !!audience.isPublic;
  }

  get paginatedAudiences(): AudienceItem[] {
    const startIndex = this.currentPage * this.pageSize;
    return this.filteredAudiences.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredAudiences.length / this.pageSize));
  }

  get statusCounts(): Record<AudienceFilterId, number> {
    const normalizedSearch = this.searchTerm.toLowerCase().trim();
    const counts: Record<AudienceFilterId, number> = {
      active: 0,
      draft: 0,
      paused: 0,
      pending: 0,
      error: 0,
      billable: 0
    };
    for (const audience of this.audiences) {
      if (this.matchesSearch(audience, normalizedSearch)) {
        counts[audience.status] += 1;
        if (this.isBillableAudience(audience)) {
          counts.billable += 1;
        }
      }
    }
    return counts;
  }

  private audiencePassesStatusFilters(audience: AudienceItem): boolean {
    if (this.statusFilters[audience.status]) {
      return true;
    }
    if (this.statusFilters.billable && this.isBillableAudience(audience)) {
      return true;
    }
    return false;
  }

  private isBillableAudience(audience: AudienceItem): boolean {
    const currentEntityId = this.getSelectedDcrFilter();
    if (!currentEntityId) {
      return false;
    }

    if (audience.isPublic === true) {
      return audience.destinations.some(dest => {
        const destEntityId = this.normalizeEntityId(dest.entityIdRaw);
        return destEntityId === currentEntityId && isInCurrentMonth(dest.lastPushRaw);
      });
    }

    const audienceEntityId = this.normalizeEntityId(audience.entityId);
    // In questo if non dovremmo mai entrare
    if (audienceEntityId !== currentEntityId) {
      return false;
    }
    // if (audience.status !== 'active' && audience.status !== 'draft') {
    //   return false;
    // }
    return isInCurrentMonth(audience.lastExtractionRaw);
  }

  changePageSize(size: Event | string | number): void {
    let numeric: number;
    if (size instanceof Event) {
      const target = size.target as HTMLSelectElement | null;
      numeric = target ? parseInt(target.value, 10) : this.pageSize;
    } else if (typeof size === 'string') {
      numeric = parseInt(size, 10);
    } else {
      numeric = size;
    }
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return;
    }
    this.pageSize = numeric;
    this.currentPage = 0;
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  toggleActionsMenu(audience: AudienceItem, event?: Event): void {
    if (audience.status !== 'error' && (!this.canManageAudienceActions || this.isAudienceQuickActionsDisabled(audience))) {
      return;
    }
    event?.stopPropagation();
    const isOpen = this.activeActionMenuId === audience.id;
    if (isOpen) {
      this.closeActionMenu();
      return;
    }

    this.activeActionMenuId = audience.id;
    this.activeActionAudience = audience;
    const trigger = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (trigger) {
      this.updateActionMenuPosition(trigger);
      // Recalculate position after menu is rendered with actual height
      setTimeout(() => {
        if (this.activeActionMenuId === audience.id && trigger) {
          this.updateActionMenuPositionWithActualHeight(trigger);
        }
      }, 0);
    } else {
      this.actionMenuPosition = { top: 0, left: 0 };
      this.actionMenuArrowOffset = 24;
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target instanceof HTMLElement ? event.target : null;

    if (this.activeActionMenuId) {
      const clickedInsideMenu = !!target?.closest('.audience-action-menu');
      const clickedTrigger = !!target?.closest('.action-trigger');
      if (!clickedInsideMenu && !clickedTrigger) {
        this.closeActionMenu();
      }
    }

    if (this.activeDestinationActionMenuId) {
      const clickedInsideDestinationMenu = !!target?.closest('.destination-action-menu');
      const clickedDestinationTrigger = !!target?.closest('.destination-action-trigger');
      if (!clickedInsideDestinationMenu && !clickedDestinationTrigger) {
        this.closeDestinationActionMenu();
      }
    }

    if (this.ownerFilterOpen) {
      const clickedFilter = !!target?.closest('.filter-dropdown') || !!target?.closest('.owner-filter-trigger');
      if (!clickedFilter) {
        this.closeOwnerFilterPanel();
      }
    }
  }

  @HostListener('window:popstate', ['$event'])
  handleBrowserPopState(event: PopStateEvent): void {
    if (this.suppressNextPopState) {
      this.suppressNextPopState = false;
    }
    const nextMode = (event.state?.audienceMode ?? 'list') as 'list' | 'create';
    if (nextMode === 'create' && this.canCreateAudience) {
      this.activateCreateView({
        creationMode: 'create',
        initialValue: this.createInitialValue,
        editingId: this.editingAudienceId,
        pushHistory: false
      });
      return;
    }
    if (nextMode === 'list' && this.mode === 'create' && this.shouldBlockCreateExit()) {
      this.restoreCreateHistoryState();
      this.openCreateExitConfirm(() => {
        this.skipCreateExitWarning = true;
        this.suppressNextPopState = true;
        window.history.back();
      }, undefined, { expectRouteChange: true });
      return;
    }
    this.exitCreateView(false);
  }

  async cloneAudience(audience: AudienceItem, event?: Event): Promise<void> {
    if (!this.canCreateAudience) {
      return;
    }
    event?.stopPropagation();

    try {
      // Show loading state
      this.preparingAudienceId = audience.id;

      // Ricarica i dati aggiornati dal server prima di clonare
      const freshAudience = await this.fetchAudienceById(audience.id);
      let audienceToClone = freshAudience ?? audience;

      // Load curve data if expandSimilarUsers is enabled
      if (audienceToClone.expandSimilarUsers && !audienceToClone.previewAccuracy) {
        audienceToClone = await this.enrichAudienceWithCurveData(audienceToClone);
      }

      await this.ensureCountriesLoaded();
      const initialValue = this.mapAudienceToFormValue(audienceToClone);
      this.activateCreateView({
        creationMode: 'clone',
        initialValue,
        editingId: null,
        pushHistory: true
      });
      this.closeActionMenu();
    } finally {
      // Clear loading state
      this.preparingAudienceId = null;
    }
  }

  confirmToggleStatus(audience: AudienceItem, event?: Event): void {
    if (!this.canManageAudienceActions) {
      return;
    }
    event?.stopPropagation();
    this.statusModalTarget = audience;
    this.statusModalOpen = true;
    this.closeActionMenu();
  }

  closeStatusModal(): void {
    if (this.statusUpdatingId) {
      return;
    }
    this.statusModalOpen = false;
    this.statusModalTarget = null;
  }

  performToggleStatus(): void {
    if (!this.canManageAudienceActions) {
      this.closeStatusModal();
      return;
    }
    const audience = this.statusModalTarget;
    if (!audience) {
      this.statusModalOpen = false;
      return;
    }
    const nextStatus: AudienceStatus = audience.status === 'active' ? 'paused' : 'active';
    const apiStatus = nextStatus === 'active' ? 'active' : 'stopped';
    const resumeEndDate = apiStatus === 'active' ? this.resolveResumeEndDate(audience) : null;
    this.statusModalOpen = false;
    this.statusUpdatingId = audience.id;
    const sub = this.audienceApi.updateAudienceStatus(audience.id, apiStatus, resumeEndDate).subscribe({
      next: () => {
        this.updateAudience(audience.id, { status: nextStatus });
        this.statusUpdatingId = null;
        this.statusModalTarget = null;
        this.notificationService.notify('success', this.translate.instant('audiences.actions.statusUpdated'));
      },
      error: (error) => {
        console.error('[audience] toggle status failed', error);
        this.statusUpdatingId = null;
        this.statusModalTarget = null;
        this.notificationService.notify('error', error?.message || this.translate.instant('audiences.actions.statusUpdateFailed'));
      }
    });
    this.subscriptions.add(sub);
  }

  async editAudience(audience: AudienceItem, event?: Event): Promise<void> {
    if (!this.canCreateAudience) {
      return;
    }
    if (!this.canManageAudienceDefinition(audience)) {
      return;
    }
    event?.stopPropagation();

    try {
      // Show loading state
      this.preparingAudienceId = audience.id;

      // Fetch fresh data first — FilterItems may not be loaded in the list view
      const freshAudience = await this.fetchAudienceById(audience.id);
      let audienceToEdit = freshAudience ?? audience;

      this.obsoleteFiltersInEdit = this.getObsoleteFilterDescriptions(audienceToEdit);

      // Load curve data if expandSimilarUsers is enabled
      if (audienceToEdit.expandSimilarUsers && !audienceToEdit.previewAccuracy) {
        audienceToEdit = await this.enrichAudienceWithCurveData(audienceToEdit);
      }

      await this.ensureCountriesLoaded();
      const initialValue = this.mapAudienceToFormValue(audienceToEdit);
      this.activateCreateView({
        creationMode: 'edit',
        initialValue,
        editingId: audienceToEdit.id,
        editingIsPublic: audienceToEdit.isPublic === true,
        pushHistory: true,
        ownerEntityId: audienceToEdit.entityId,
        ownerEntityType: audienceToEdit.entityType
      });
      this.closeActionMenu();
    } finally {
      // Clear loading state
      this.preparingAudienceId = null;
    }
  }

  confirmExport(audience: AudienceItem, event?: Event): void {
    if (!this.canManageAudienceActions) {
      return;
    }
    event?.stopPropagation();
    this.exportModalTarget = audience;
    this.exportModalOpen = true;
    this.closeActionMenu();
  }

  closeExportModal(): void {
    if (this.exportingAudienceId) {
      return;
    }
    this.exportModalOpen = false;
    this.exportModalTarget = null;
  }

  performExport(): void {
    if (!this.canManageAudienceActions) {
      this.closeExportModal();
      return;
    }
    const audience = this.exportModalTarget;
    if (!audience) {
      this.exportModalOpen = false;
      return;
    }
    this.exportModalOpen = false;
    this.exportingAudienceId = audience.id;
    const sub = this.audienceApi.extractAudience(audience.id).subscribe({
      next: () => {
        this.exportingAudienceId = null;
        this.exportModalTarget = null;
        this.updateAudience(audience.id, { isRunning: true });
        this.notificationService.notify('success', this.translate.instant('audiences.actions.exportStarted', { name: audience.name }));
        const timeoutHandle = setTimeout(() => {
          this.extractionTimeouts.delete(audience.id);
          this.updateAudience(audience.id, { isRunning: false });
          this.notificationService.notify('warning', this.translate.instant('audiences.actions.exportTimeout', { name: audience.name }));
        }, 15 * 60 * 1000);
        this.extractionTimeouts.set(audience.id, timeoutHandle);
      },
      error: (error) => {
        console.error('[audience] export failed', error);
        this.exportingAudienceId = null;
        this.exportModalTarget = null;
        this.notificationService.notify('error', this.resolveActionErrorMessage(error, 'audiences.actions.exportFailed'));
      }
    });
    this.subscriptions.add(sub);
  }

  openDestinationModal(audience: AudienceItem, event?: Event): void {
    if (!this.canExportAudience(audience)) {
      return;
    }
    event?.stopPropagation();
    this.destinationModalTarget = audience;
    this.destinationModalOpen = true;
    this.closeActionMenu();
  }

  closeDestinationModal(): void {
    this.destinationModalOpen = false;
    this.destinationModalTarget = null;
  }

  handleDestinationActivated(): void {
    // Refresh audiences to update destination status
    this.fetchAudiences(this.destinationModalTarget?.id, true);
  }

  checkAudienceOverlap(audience: AudienceItem, event?: Event): void {
    event?.stopPropagation();
    this.overlapLoadingId = audience.id;
    this.overlapSourceAudienceId = audience.id;
    this.overlapModalOpen = true;
    this.overlapResults = [];
    const sub = this.audienceApi.getAudienceIntersections(audience.id).subscribe({
      next: (result) => {
        this.overlapResults = this.enrichOverlapResults(result);
        this.overlapLoadingId = null;
        this.notificationService.notify('success', this.translate.instant('audiences.actions.overlapLoaded', { name: audience.name }));
      },
      error: (error) => {
        console.error('[audience] overlap failed', error);
        this.overlapLoadingId = null;
        this.notificationService.notify('error', error?.message || this.translate.instant('audiences.actions.overlapFailed'));
      }
    });
    this.subscriptions.add(sub);
    this.closeActionMenu();
  }

  confirmDelete(audience: AudienceItem, event?: Event): void {
    if (!this.canManageAudienceActions) {
      return;
    }
    event?.stopPropagation();
    this.deleteTargetAudience = audience;
    this.deleteModalOpen = true;
    this.closeActionMenu();
  }

  closeDeleteModal(): void {
    if (this.deletingAudienceId) {
      return;
    }
    this.deleteModalOpen = false;
    this.deleteTargetAudience = null;
  }

  performDelete(): void {
    if (!this.canManageAudienceActions) {
      this.closeDeleteModal();
      return;
    }
    const audience = this.deleteTargetAudience;
    if (!audience) {
      this.deleteModalOpen = false;
      return;
    }
    this.deleteModalOpen = false;
    this.deletingAudienceId = audience.id;
    const sub = this.audienceApi.deleteAudience(audience.id).subscribe({
      next: () => {
        this.audiences = this.audiences.filter((item) => item.id !== audience.id);
        if (this.selectedAudience?.id === audience.id) {
          this.selectedAudience = null;
        }
        this.ensureSelectedAudienceVisible();
        this.notificationService.notify('success', this.translate.instant('audiences.actions.deleted', { name: audience.name }));
        this.deletingAudienceId = null;
        this.deleteTargetAudience = null;
      },
      error: (error) => {
        console.error('[audience] delete failed', error);
        this.deletingAudienceId = null;
        this.deleteTargetAudience = null;
        this.notificationService.notify('error', error?.message || this.translate.instant('audiences.actions.deleteFailed'));
      }
    });
    this.subscriptions.add(sub);
  }

  reportAudienceError(audience: AudienceItem, event?: Event): void {
    event?.stopPropagation();
    if (this.reportingErrorAudienceId) return;
    this.reportingErrorAudienceId = audience.id;
    this.closeActionMenu();
    const sub = this.audienceApi.reportAudienceError(audience.id).subscribe({
      next: () => {
        this.reportingErrorAudienceId = null;
        this.notificationService.notify('success', this.translate.instant('audiences.actions.reportErrorSent'));
      },
      error: (error) => {
        console.error('[audience] report error failed', error);
        this.reportingErrorAudienceId = null;
        this.notificationService.notify('error', error?.message || this.translate.instant('audiences.actions.reportErrorFailed'));
      }
    });
    this.subscriptions.add(sub);
  }

  private matchesSearch(audience: AudienceItem, normalizedSearch: string): boolean {
    if (!normalizedSearch) {
      return true;
    }
    const haystack = `${audience.name} ${audience.createdBy} ${audience.id}`.toLowerCase();
    return haystack.includes(normalizedSearch);
  }

  private createStatusFilters(): Record<AudienceFilterId, boolean> {
    return {
      active: true,
      draft: true,
      paused: false,
      pending: false,
      error: false,
      billable: false
    };
  }

  private closeActionMenu(): void {
    this.activeActionMenuId = null;
    this.activeActionAudience = null;
    this.actionMenuArrowOffset = 24;
  }

  private updateAudience(id: string, changes: Partial<AudienceItem>): void {
    this.audiences = this.audiences.map((audience) =>
      audience.id === id ? { ...audience, ...changes } : audience
    );
    const updatedAudience = this.audiences.find((audience) => audience.id === id) ?? null;
    if (updatedAudience) {
      this.syncCurveTrackingState(updatedAudience);
    }
    // A status change (e.g. active <-> paused) changes whether the audience can be referenced
    // by a rule-based one.
    this.rebuildRuleAudienceOptions();
    this.syncSelectedAudience(id);
    this.syncDestinationModalTarget(id);
  }

  private syncSelectedAudience(id: string): void {
    if (this.selectedAudience?.id !== id) {
      return;
    }
    this.selectedAudience = this.audiences.find((audience) => audience.id === id) ?? null;
  }

  private syncDestinationModalTarget(id: string): void {
    if (this.destinationModalTarget?.id !== id) {
      return;
    }
    this.destinationModalTarget = this.audiences.find((audience) => audience.id === id) ?? null;
    if (!this.destinationModalTarget) {
      this.destinationModalOpen = false;
    }
  }

  private shouldAttemptCurveDataLoad(audience: AudienceItem): boolean {
    if (!audience.expandSimilarUsers || audience.previewAccuracy) {
      return false;
    }

    const audienceType = audience.frontendPreview?.AudienceType ?? null;

    if (audience.status === 'draft') {
      return audienceType === AUDIENCE_TYPES.CUSTOM || audienceType === AUDIENCE_TYPES.INTERESTS;
    }

    if (audience.status !== 'active') {
      return false;
    }

    if (audienceType === AUDIENCE_TYPES.CUSTOM) {
      return true;
    }

    if (audienceType === AUDIENCE_TYPES.INTERESTS) {
      // Prova sempre a caricare i dati della curva per interests, anche senza audienceQualityIndex
      return true;
    }

    return isCustomAudience(audience) || audience.audienceQualityIndex !== null;
  }

  private syncCurveTrackingState(audience: AudienceItem): void {
    if (audience.previewAccuracy) {
      this.curveAttemptedAudienceIds.add(audience.id);
      this.curvePendingAudienceIds.delete(audience.id);
      return;
    }

    if (this.curvePendingAudienceIds.has(audience.id)) {
      return;
    }

    this.curvePendingAudienceIds.delete(audience.id);
  }

  private resetCurveLoadState(audienceId: string): void {
    this.curvePendingAudienceIds.delete(audienceId);
    this.curveAttemptedAudienceIds.delete(audienceId);
  }

  private calculateMenuPosition(
    trigger: HTMLElement,
    menuElement: HTMLElement | null,
    menuWidth: number,
    estimatedMenuHeight: number
  ): { top: number; left: number; placement: 'below' | 'above'; arrowOffset: number } {
    const rect = trigger.getBoundingClientRect();
    const menuHeight = menuElement?.offsetHeight || estimatedMenuHeight;
    const padding = 16;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const preferredLeft = rect.right - menuWidth;
    const maxLeft = viewportWidth - menuWidth - padding;
    const left = Math.min(Math.max(preferredLeft, padding), maxLeft);

    // Check if there's enough space below the trigger
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top: number;
    let placement: 'below' | 'above' = 'below';

    if (spaceBelow >= menuHeight + 16) {
      // Enough space below - open downward
      top = rect.bottom + 8;
      placement = 'below';
    } else if (spaceAbove >= menuHeight + 16) {
      // Not enough space below but enough above - open upward
      top = rect.top - menuHeight - 8;
      placement = 'above';
    } else if (spaceBelow > spaceAbove) {
      // Not enough space in either direction - use the side with more space
      top = rect.bottom + 8;
      placement = 'below';
    } else {
      top = rect.top - menuHeight - 8;
      placement = 'above';
    }

    const buttonCenter = rect.left + rect.width / 2;
    const arrowPadding = 24;
    const relativeCenter = buttonCenter - left;
    const arrowOffset = Math.min(
      Math.max(relativeCenter, arrowPadding),
      menuWidth - arrowPadding
    );

    return { top, left, placement, arrowOffset };
  }

  private updateActionMenuPosition(trigger: HTMLElement): void {
    if (typeof window === 'undefined') {
      this.actionMenuPosition = { top: 0, left: 0 };
      this.actionMenuArrowOffset = 24;
      this.actionMenuPlacement = 'below';
      return;
    }

    const { top, left, placement, arrowOffset } = this.calculateMenuPosition(trigger, null, 240, 280);
    this.actionMenuPlacement = placement;
    this.actionMenuPosition = { top, left };
    this.actionMenuArrowOffset = arrowOffset;
  }

  private updateActionMenuPositionWithActualHeight(trigger: HTMLElement): void {
    if (typeof window === 'undefined') {
      return;
    }

    const menuElement = document.querySelector('.audience-action-menu') as HTMLElement | null;
    if (!menuElement) {
      return;
    }

    const { top, left, placement, arrowOffset } = this.calculateMenuPosition(trigger, menuElement, 240, 280);
    this.actionMenuPlacement = placement;
    this.actionMenuPosition = { top, left };
    this.actionMenuArrowOffset = arrowOffset;
  }

  private updateDestinationActionMenuPosition(trigger: HTMLElement): void {
    if (typeof window === 'undefined') {
      this.destinationActionMenuPosition = { top: 0, left: 0 };
      this.destinationActionMenuArrowOffset = 24;
      this.destinationActionMenuPlacement = 'below';
      return;
    }

    const { top, left, placement, arrowOffset } = this.calculateMenuPosition(trigger, null, 240, 200);
    this.destinationActionMenuPlacement = placement;
    this.destinationActionMenuPosition = { top, left };
    this.destinationActionMenuArrowOffset = arrowOffset;
  }

  /**
   * Options for the "Audience" filter of a rule-based audience: only active ones can be
   * referenced. Derived from `this.audiences`, so it must be refreshed by every path that
   * mutates that list (list reload and real-time upsert/delete alike).
   * The audience being edited is excluded by `ruleAudienceOptionsForForm`, not here: keying
   * the exclusion on the list selection hid the audience just created, which gets selected
   * automatically after the save.
   */
  private rebuildRuleAudienceOptions(): void {
    this.ruleAudienceOptions = this.audiences
      .filter(aud => aud.status === 'active')
      .map(aud => ({ id: aud.id, name: aud.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private fetchAudiences(keepSelectionId?: string, preserveState?: boolean): void {
    this.isLoading = true;
    this.loadingError = null;
    const sub = this.audienceApi.getAudiences().subscribe({
      next: (list) => {
        const existingAudiencesById = new Map(this.audiences.map(audience => [audience.id, audience]));
        const mapped = list.map((aud) => {
          const nextAudience = this.mapApiAudience(aud);
          const existingAudience = existingAudiencesById.get(nextAudience.id);
          if (!nextAudience.previewAccuracy && existingAudience?.previewAccuracy) {
            return {
              ...nextAudience,
              previewAccuracy: existingAudience.previewAccuracy
            };
          }
          return nextAudience;
        });
        this.entityOwnerService.ensureOwners([
          ...mapped.map(a => ({ id: a.entityId })),
          ...mapped.flatMap(a => (a.destinations ?? []).map(dest => ({ id: dest.entityIdRaw })))
        ]);
        const filteredAudiences = this.filterAudiencesByDcr(mapped);
        this.audiences = [...filteredAudiences];
        this.rebuildRuleAudienceOptions();
        this._audiencesLoaded = true;
        const desiredId = keepSelectionId || this.selectedAudience?.id;
        const desiredAudience = desiredId ? mapped.find(aud => aud.id === desiredId) : null;
        this.selectedAudience = desiredAudience && this.audiencePassesStatusFilters(desiredAudience) ? desiredAudience : null;
        if (this.destinationModalTarget) {
          const modalTarget = mapped.find(aud => aud.id === this.destinationModalTarget?.id) ?? null;
          this.destinationModalTarget = modalTarget;
          if (!modalTarget) {
            this.destinationModalOpen = false;
          }
        }
        this.isLoading = false;
        if (!preserveState) {
          this.resetPagination();
          this.ensureSelectedAudienceVisible();
        }

        filteredAudiences.forEach(audience => {
          this.syncCurveTrackingState(audience);
          // Curve data is now loaded only when entering edit/clone/approve
        });
      },
      error: (error) => {
        console.error('[audience] list failed', error);
        this.loadingError = error?.message || this.translate.instant('audiences.list.error', { value: 'Unable to load audiences' });
        this.audiences = [];
        this.selectedAudience = null;
        this.isLoading = false;
        this._audiencesLoaded = true;
      }
    });
    this.subscriptions.add(sub);
  }

  /** Campi di AudienceItem aggiornati dallo snapshot allegato all'SSE di fine estrazione. */
  private mapExtractionSnapshot(snapshot: Record<string, any>): Partial<AudienceItem> {
    return {
      status: normalizeStatus(snapshot['Status']),
      isRunning: !!snapshot['IsRunning'],
      volume: formatVolume(snapshot['Volumes']),
      volumeRaw: toNumericValue(snapshot['Volumes']),
      lastExtraction: formatDate(snapshot['LastExtraction']),
      lastExtractionRaw: snapshot['LastExtraction'] ?? null,
      nextExtraction: formatDate(snapshot['NextSchedulationDate']),
      nextExtractionRaw: snapshot['NextSchedulationDate'] ?? null,
      audienceQualityIndex: toNumericValue(snapshot['AudienceQualityIndex'])
    };
  }

  private async fetchAudienceById(audienceId: string): Promise<AudienceItem | null> {
    if (!audienceId) {
      return null;
    }
    try {
      const response = await firstValueFrom(this.audienceApi.getAudiences({ audienceId }));
      if (response?.length) {
        return await this.enrichAudienceWithCurveData(this.mapApiAudience(response[0]));
      }
    } catch (error) {
      console.error('[audience] failed to refresh audience after create', error);
    }
    return null;
  }

  // Cambio della modalità di espansione dal form (admin): ricarica la curva della versione scelta
  // e la ripassa al form come anteprima live. Per audience nuove/clone (senza ID salvato) non c'è
  // curva da mostrare finché non vengono estratte.
  async onExpandVersionChanged(weighted: boolean): Promise<void> {
    const audienceId = this.editingAudienceId;
    if (!audienceId) {
      return;
    }
    try {
      const variant: 'standard' | 'weighted' = weighted ? 'weighted' : 'standard';
      const curve = await firstValueFrom(this.audienceApi.getCurveVolumeByDistance(audienceId, variant));
      const previewAccuracy = mapCurveVolumeToPreviewAccuracy(curve, null);
      const fullRangeAccuracy = mapCurveVolumeToPreviewAccuracy(curve, null, true);
      this.audienceCreateForm?.applyExternalPreview(
        previewAccuracy ? mapPreviewAccuracyToForm(previewAccuracy) : null,
        fullRangeAccuracy ? mapPreviewAccuracyToForm(fullRangeAccuracy) : null
      );
    } catch {
      // Variante non ancora calcolata (o errore): niente curva da mostrare per questo metodo.
      this.audienceCreateForm?.applyExternalPreview(null);
    }
  }

  private async loadCurveDataForAudience(audience: AudienceItem): Promise<void> {
    if (!this.shouldAttemptCurveDataLoad(audience)) {
      return;
    }

    if (this.curvePendingAudienceIds.has(audience.id) || this.curveAttemptedAudienceIds.has(audience.id)) {
      return;
    }

    this.curvePendingAudienceIds.add(audience.id);

    try {
      const enriched = await this.enrichAudienceWithCurveData(audience);
      this.curveAttemptedAudienceIds.add(audience.id);

      if (!enriched.previewAccuracy) {
        return;
      }

      this.updateAudience(enriched.id, enriched);
      if (this.selectedAudience?.id === enriched.id) {
        this.selectedAudience = enriched;
      }
    } finally {
      this.curvePendingAudienceIds.delete(audience.id);
    }
  }

  private async enrichAudienceWithCurveData(audience: AudienceItem): Promise<AudienceItem> {
    if (!this.shouldAttemptCurveDataLoad(audience)) {
      return audience;
    }

    try {
      const curve = await firstValueFrom(this.audienceApi.getCurveVolumeByDistance(audience.id));
      const previewAccuracy = mapCurveVolumeToPreviewAccuracy(curve, audience.audienceQualityIndex);
      if (!previewAccuracy) {
        return audience;
      }
      const fullRangePreviewAccuracy = mapCurveVolumeToPreviewAccuracy(curve, audience.audienceQualityIndex, true);
      return {
        ...audience,
        previewAccuracy,
        fullRangePreviewAccuracy
      };
    } catch (error) {
      console.error('[audience] failed to load curve volume by distance', error);
      return audience;
    }
  }

  private loadCustomImports(): void {
    const sub = this.customImportApi.getCustomImports().subscribe({
      next: (imports: CustomImportDto[]) => {
        const filteredImports = this.filterCustomImportDtos(imports);
        this.customImportOptions = [
          ...filteredImports
            .filter(item => item.Status !== 0)
            .map(item => ({
              id: item.ID,
              name: item.Name
            }))
        ];
        this._customImportsLoaded = true;
      },
      error: (error) => {
        console.error('[audience] custom imports load failed', error);
        this.customImportOptions = [];
        this._customImportsLoaded = true;
      }
    });
    this.subscriptions.add(sub);
  }

  private loadSourceGroups(): void {
    const sub = this.groupsApi.getGroups().subscribe({
      next: (groups: GroupDto[]) => {
        this.sourceGroupMap = new Map(groups.map(group => [group.ID, group]));
      },
      error: (error) => {
        console.error('[audience] source groups load failed', error);
        this.sourceGroupMap.clear();
      }
    });
    this.subscriptions.add(sub);

    const memberSub = this.groupsApi.isTNCIDPlusMember().subscribe({
      next: (isMember) => { this.isEntityTNCIDPlusMember = isMember; },
      error: () => { this.isEntityTNCIDPlusMember = false; }
    });
    this.subscriptions.add(memberSub);
  }

  private loadCountries(): void {
    void this.ensureCountriesLoaded();
  }

  // Caches the countries request so the geo filter payload can await it before
  // resolving a country name to its code, instead of racing the ngOnInit prefetch.
  private ensureCountriesLoaded(): Promise<void> {
    if (this.countriesLoaded) {
      return Promise.resolve();
    }
    if (!this.countriesLoadPromise) {
      this.countriesLoadPromise = firstValueFrom(
        this.audienceApi.getCountries().pipe(
          catchError(error => {
            console.error('[audience] countries load failed', error);
            return of<CountryOption[]>([]);
          })
        )
      ).then(countries => {
        this.countryOptions = countries;
        this.countriesLoaded = true;
      });
    }
    return this.countriesLoadPromise;
  }

  private mapApiAudience(api: AudienceApiResponse, options?: { ignorePreviewData?: boolean }): AudienceItem {
    const audience = mapApiAudience(api, this.destinationLookups, options);
    if (api.Status === 'draft' || api.Status === 'preview') {
      console.log('[mapApiAudience] Draft audience, previewAccuracy:', audience.previewAccuracy, 'ignorePreviewData:', options?.ignorePreviewData);
    }
    return audience;
  }

  private mapActivationDestination(item: ActivationItemApi): AudienceDestination {
    return mapActivationDestination(item, this.destinationLookups);
  }

  private get destinationLookups(): DestinationLookups {
    return { destinationNames: this.destinationNames, destinationTypeInfo: this.destinationTypeInfo };
  }

  private mapAudienceToFormValue(audience: AudienceItem): AudienceCreateFormValue {
    return mapAudienceToFormValue(audience, {
      providers: this.providerMap,
      resolveCountryName: code => this.resolveCountryName(code)
    });
  }

  private resolvePublisherToProvider(publisherEntityId: string): string | null {
    return resolvePublisherToProvider(this.providerMap, publisherEntityId);
  }

  private inferHasIncompatibleFilters(filterItems: AudienceFilterItem[], queryComposition?: string | null): boolean {
    return inferHasIncompatibleFilters(filterItems, queryComposition, this.providerMap);
  }

  private buildInterestFilters(formValue: AudienceCreateFormValue, normalizedStart: string, normalizedEnd: string): FilterBuildResult {
    return buildInterestFilters(formValue, normalizedStart, normalizedEnd, {
      allLocationsLabel: this.translate.instant('audiences.create.filters.locationAll'),
      resolveCountryCode: name => this.resolveCountryCode(name)
    });
  }

  private buildRuleBasedFilters(formValue: AudienceCreateFormValue, normalizedStart: string, normalizedEnd: string): FilterBuildResult {
    return buildRuleBasedFilters(formValue, normalizedStart, normalizedEnd, this.providerMap);
  }

  private resolveCountryCode(countryName: string): string {
    const match = this.countryOptions.find(
      option => option.name.toLowerCase() === countryName.trim().toLowerCase()
    );
    return match ? match.code : countryName;
  }

  private resolveCountryName(countryCode: string): string {
    const match = this.countryOptions.find(
      option => option.code.toLowerCase() === countryCode.trim().toLowerCase()
    );
    return match ? match.name : countryCode;
  }

  private loadProviders(): void {
    const sub = this.providersApi.getProviders(undefined, { skipDcr: this.shouldSkipDcrRuleFilters }).subscribe({
      next: (providers: ProviderDto[]) => {
        // Save complete providers in map for later use
        this.providerMap = new Map(providers.map(p => [p.ID, p]));

        const eligibleProviders = providers
          .filter(provider => this.isActiveProvider(provider))
          .filter(provider => this.shouldSkipDcrRuleFilters || this.matchesSelectedDcr(provider));

        // Group by PublisherID: if a provider exists with ID === PublisherID, show only that one
        // (it represents the publisher itself) and hide all other providers in the same group.
        const byPublisher = new Map<string, ProviderDto[]>();
        eligibleProviders.forEach(p => {
          const key = p.PublisherID ?? `__no_pub__${p.ID}`;
          if (!byPublisher.has(key)) byPublisher.set(key, []);
          byPublisher.get(key)!.push(p);
        });

        const filteredProviders: ProviderDto[] = [];
        byPublisher.forEach((group, pubId) => {
          const publisherAsProvider = group.find(p => p.ID === pubId);
          if (publisherAsProvider) {
            filteredProviders.push(publisherAsProvider);
          } else {
            filteredProviders.push(...group);
          }
        });

        this.sourceGroupOptions = filteredProviders.map(provider => ({
          id: provider.ID,
          name: provider.Name?.trim() || provider.ID,
          type: 'provider'
        }));
        this._providersLoaded = true;
      },
      error: (error) => {
        console.error('[audience] providers load failed', error);
        this.sourceGroupOptions = [];
        this.providerMap.clear();
        this._providersLoaded = true;
      }
    });
    this.subscriptions.add(sub);
  }

  private isActiveProvider(provider: ProviderDto): boolean {
    if (!provider?.ID || !provider?.Name) {
      return false;
    }
    if (provider.Status === null || provider.Status === undefined) {
      return true;
    }
    if (typeof provider.Status === 'number') {
      return provider.Status !== 0;
    }
    const normalized = provider.Status.toString().trim().toLowerCase();
    return normalized !== '0' && normalized !== 'inactive';
  }

  private matchesSelectedDcr(provider: ProviderDto): boolean {
    if (this.authService.isTNCDataSelection()) {
      return true;
    }
    const activeDcrId = this.authService.getActiveDcrId();
    if (!activeDcrId) {
      return true;
    }
    const normalizedSelected = activeDcrId.toString().trim().toLowerCase();
    if (!normalizedSelected) {
      return true;
    }
    const publisherId = provider.PublisherID ? provider.PublisherID.toString().trim().toLowerCase() : '';
    return publisherId === normalizedSelected;
  }

  private loadDestinations(): void {
    const sub = this.destinationsApi.getDestinations({ skipDcr: this.hasGlobalDataAccess }).subscribe({
      next: (destinations: DestinationDto[]) => {
        const available = destinations.filter(dest => dest && dest.Status !== 'deleted');
        // Full (non DCR-filtered) index: metadata source (type, owner) for activations
        // pointing to destinations the current user has no access to.
        this.allDestinationsById = new Map(available.map(dest => [String(dest.ID), dest]));
        const filtered = this.filterDestinationDtosByDcr(available);
        this.availableDestinations = [...filtered];
        this.destinationNames = new Map(this.availableDestinations.map(dest => [String(dest.ID), dest.Name]));
        this.destinationTypeInfo = new Map(available.map(dest => [String(dest.ID), { typeId: dest.DestinationTypeID, typeName: dest.DestinationTypeName }]));
        this.entityOwnerService.ensureOwners(available.map(dest => ({ id: dest.EntityID })));
        this.audiences = [...this.audiences.map(audience => this.applyDestinationNames(audience))];
        if (this.selectedAudience) {
          const refreshed = this.audiences.find(aud => aud.id === this.selectedAudience?.id);
          if (refreshed) {
            this.selectedAudience = refreshed;
          }
        }
        if (this.destinationModalTarget) {
          const refreshedModal = this.audiences.find(aud => aud.id === this.destinationModalTarget?.id) ?? null;
          this.destinationModalTarget = refreshedModal;
          if (!refreshedModal) {
            this.destinationModalOpen = false;
          }
        }
      },
      error: (error) => {
        console.error('[audience] destinations load failed', error);
        this.availableDestinations = [];
        this.allDestinationsById.clear();
        this.destinationNames.clear();
        this.destinationTypeInfo.clear();
      }
    });
    this.subscriptions.add(sub);
  }

  private handleActiveDcrChange(): void {
    // If in create/edit/clone mode, check for unsaved changes and show confirmation popup
    // mode === 'create' covers all three cases: create, edit, and clone (based on creationMode)
    if (this.mode === 'create' && this.shouldBlockCreateExit()) {
      this.openCreateExitConfirm(async () => {
        // User confirmed, exit create view and reload data
        this.skipCreateExitWarning = true;
        this.exitCreateView(true);
        this.audiences = [];
        // Selection belongs to the previous entity: keeping it would make fetchAudiences
        // restore it and jump the pagination to its page, so the reloaded list would
        // start mid-alphabet instead of at page 1.
        this.selectedAudience = null;
        this._audiencesLoaded = false;
        this._customImportsLoaded = false;
        this._providersLoaded = false;
        this.fetchAudiences();
        this.loadCustomImports();
        this.loadSourceGroups();
        this.loadProviders();
        this.loadDestinations();
      });
      return;
    }

    // If in create/edit/clone mode but no changes, exit silently
    if (this.mode === 'create') {
      this.exitCreateView(true);
    }

    this.audiences = [];
    // See the note above: the selection of the previous entity must not survive the
    // reload, otherwise the list reopens on the selected audience's page.
    this.selectedAudience = null;
    this._audiencesLoaded = false;
    this._customImportsLoaded = false;
    this._providersLoaded = false;
    this.fetchAudiences();
    this.loadCustomImports();
    this.loadSourceGroups();
    this.loadProviders();
    this.loadDestinations();
  }

  private filterAudiencesByDcr(audiences: AudienceItem[]): AudienceItem[] {
    if (this.shouldSkipDcrRuleFilters) {
      return audiences;
    }

    const filterId = this.getSelectedDcrFilter();

    if (!filterId) {
      return audiences;
    }

    return audiences.filter(audience => {
      const normalizedEntityId = this.normalizeEntityId(audience.entityId);
      return normalizedEntityId === filterId || audience.isPublic === true;
    });
  }

  private filterCustomImportDtos(imports: CustomImportDto[]): CustomImportDto[] {
    if (this.shouldSkipDcrRuleFilters || this.authService.isTNCDataSelection()) {
      return imports;
    }

    const creationDcrId = this.authService.getAudienceCreationDcrId();
    if (creationDcrId) {
      return imports.filter(item => this.normalizeEntityId(item.OwnerEntityID) === this.normalizeEntityId(creationDcrId));
    }

    const filterId = this.getSelectedDcrFilter();
    if (!filterId) {
      return imports;
    }

    return imports.filter(item => this.normalizeEntityId(item.OwnerEntityID) === filterId);
  }

  private filterDestinationDtosByDcr(destinations: DestinationDto[]): DestinationDto[] {
    if (this.hasGlobalDataAccess) {
      return destinations;
    }

    const filterId = this.getSelectedDcrFilter();
    if (!filterId) {
      return destinations;
    }
    return destinations.filter(destination => this.normalizeEntityId(destination.EntityID) === filterId);
  }

  /** Single source of truth for the entity-selector scope (see AuthService.getEntityScopeFilterId). */
  private getSelectedDcrFilter(): string | null {
    return this.authService.getEntityScopeFilterId();
  }

  private normalizeEntityId(value?: string | null): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  copyAudienceValue(value: string | null | undefined): void {
    if (!value) {
      return;
    }
    const text = value.toString();
    const copyPromise = (typeof navigator !== 'undefined' && navigator.clipboard?.writeText)
      ? navigator.clipboard.writeText(text)
      : this.fallbackCopyText(text);

    copyPromise.then(() => {
      this.notificationService.notify('success', this.translate.instant('audiences.details.copySuccess'));
    }).catch(() => {
      this.notificationService.notify('error', this.translate.instant('audiences.details.copyError'));
    });
  }

  private fallbackCopyText(text: string): Promise<void> {
    if (typeof document === 'undefined') {
      return Promise.reject(new Error('Unavailable'));
    }
    return new Promise((resolve, reject) => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (successful) {
          resolve();
        } else {
          reject(new Error('Copy failed'));
        }
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Copy failed'));
      }
    });
  }

  private extractTagDescription(tags?: string[] | null): string {
    if (!tags || !tags.length) {
      return '';
    }
    return tags.join('\n');
  }

  /**
   * Messaggio da mostrare per un'azione fallita: il caso sandbox ha una stringa tradotta dedicata
   * (il codice arriva dall'API, il messaggio dell'API è solo in inglese), poi il messaggio
   * dell'API se c'è, infine il fallback del caso d'uso.
   */
  private resolveActionErrorMessage(error: any, fallbackKey: string): string {
    if (isSandboxError(error)) {
      return this.translate.instant('errors.sandboxEntity');
    }
    return error?.message || this.translate.instant(fallbackKey);
  }

  private async buildUpdatePayload(
    formValue: AudienceCreateFormValue,
    providedTags: string[] | null,
    options?: { previewOnly?: boolean; forceDraft?: boolean }
  ): Promise<AudienceUpdatePayload | null> {
    if (!this.editingAudienceId) {
      return null;
    }
    let existing = this.audiences.find(aud => aud.id === this.editingAudienceId) ?? null;
    if (!existing && this.selectedAudience?.id === this.editingAudienceId) {
      existing = this.selectedAudience;
    }
    if (!existing) {
      existing = await this.fetchAudienceById(this.editingAudienceId);
    }
    const payload = await this.buildAudiencePayload(formValue, existing, providedTags, options);
    if (!payload) {
      return null;
    }
    return {
      ...payload,
      ID: this.editingAudienceId
    };
  }

  private async buildAudiencePayload(
    formValue: AudienceCreateFormValue,
    existingAudience?: AudienceItem | null,
    providedTags?: string[] | null,
    options?: { previewOnly?: boolean; forceDraft?: boolean }
  ): Promise<AudienceCreatePayload | null> {
    // Ensure country name->code mapping is available before building the geo filter below.
    await this.ensureCountriesLoaded();

    const wasPublic = existingAudience?.isPublic === true
      || (this.selectedAudience?.id === this.editingAudienceId && this.selectedAudience?.isPublic === true)
      || this.editingAudienceIsPublic === true;
    const isPublicAudience = this.canMakePublicAudience
      ? !!formValue.isPublic
      : wasPublic;
    const currentUser = this.authService.currentUserValue;
    const creationDcrId = this.authService.getAudienceCreationDcrId();
    const fallbackEntity = this.resolveEntity(currentUser, creationDcrId);
    let entityDetails = existingAudience
      ? this.resolveExistingEntity(existingAudience, fallbackEntity, creationDcrId)
      : fallbackEntity;

    if (!entityDetails && creationDcrId) {
      const entityType = currentUser?.dataAccessLevel ?? 'publisher';
      entityDetails = {
        entity: {
          EntityID: creationDcrId,
          EntityType: entityType
        },
        sourceGroupId: creationDcrId
      };
    }

    if (!entityDetails) {
      return null;
    }

    const startDate = normalizeDate(formValue.startDate) ?? todayIsoDate();
    const endDate = resolveEndDate(formValue);
    const isRuleBased = this.isRuleBasedForm(formValue);
    const isCustomQuery = formValue.type === AUDIENCE_TYPES.CUSTOM_QUERY;

    const TNCID_PLUS_GROUP_ID = '5f6efd54-1488-11f0-9621-42010a9c0008';

    // Gestione ExpandSourceGroupID
    // Default: sempre TNCID_PLUS_GROUP_ID
    entityDetails.sourceGroupId = TNCID_PLUS_GROUP_ID;

    // Eccezione: Interest-based (AudienceType=='interests') con first-party data (useAllNetworkData==false)
    if (!isRuleBased && !isCustomQuery && !formValue.useAllNetworkData) {
      // Interest Based con First Party: usa il DCR group FIRST_PARTY della DCR corrente
      if (creationDcrId) {
        const firstPartyGroupId = this.findFirstPartyGroupId(creationDcrId);
        if (firstPartyGroupId) {
          entityDetails.sourceGroupId = firstPartyGroupId;
        } else {
          console.warn('[audience] First Party DCR group not found for DCR:', creationDcrId);
          // Fallback to TNCID_PLUS_GROUP_ID (già assegnato sopra)
        }
      }
    }

    let filterBuild: ReturnType<typeof this.buildRuleBasedFilters>;
    if (isCustomQuery) {
      filterBuild = {
        filterItems: [{ ID: 1, filterType: 'Custom', filterValue: null }],
        queryComposition: 'Custom#1',
        timerange: existingAudience?.globalTimerange ?? 91,
        selectedSourceGroups: [],
        hasRuleFilters: false,
      };
    } else {
      filterBuild = isRuleBased
        ? this.buildRuleBasedFilters(formValue, startDate, endDate)
        : this.buildInterestFilters(formValue, startDate, endDate);
    }

    if (!filterBuild) {
      return null;
    }

    if (isRuleBased && !filterBuild.hasRuleFilters) {
      return null;
    }

    if (isRuleBased) {
      ensureSourceFiltersAreSerialized(filterBuild);
    }

    const schedulation = mapFrequency(formValue.frequency);
    const extendVolumes = this.resolveExtendVolumes(formValue);
    const tags = resolvePayloadTags(formValue, {
      isRuleBased,
      providedTags,
      existingTags: existingAudience?.tags ?? null
    });

    // Extract SourceGroupIDs from filters
    const filterSourceGroupIds = await this.extractSourceGroupIDsFromFilters(filterBuild.filterItems);

    const allSourceGroupIds = new Set<string>();

    if (!isRuleBased) {
      // AudienceType='interests': ExpandSourceGroupID always goes into SourceGroupIDs
      if (entityDetails.sourceGroupId) {
        allSourceGroupIds.add(entityDetails.sourceGroupId);
      }
    }
    // AudienceType='custom': SourceGroupIDs comes only from filters (ExpandSourceGroupID is
    // passed separately but must not be injected here regardless of expandSimilarUsers)

    // Add all source groups from filters
    filterSourceGroupIds.forEach(id => allSourceGroupIds.add(id));

    const shouldExpandSimilarUsers = resolveExpandSimilarUsers({
      isRuleBased,
      isCustomQuery,
      formValue: formValue.expandSimilarUsers,
      tags
    });

    const audienceQuality = options?.forceDraft ? null : formValue.audienceQuality;
    const draftExtendVolumes = options?.forceDraft ? null : extendVolumes;
    // Filtri interests (tag/età/genere/paese/regioni) cambiati in edit: la curva salvata non
    // descrive più questa audience, quindi l'indice va azzerato e la curva eliminata lato backend.
    const resetQualityIndex = !isRuleBased && !isCustomQuery && !!formValue.interestFiltersChanged && !!existingAudience;
    const audienceQualityIndex = (options?.forceDraft || resetQualityIndex)
      ? null
      : this.resolveAudienceQualityIndex(formValue, existingAudience);

    const payload = {
      Name: formValue.name,
      Status: existingAudience?.status === 'draft' && !options?.previewOnly ? 'active' : undefined,
      IsPublic: isPublicAudience,
      Description: formValue.description,
      Brief: formValue.aiDescription?.toString().trim() || undefined,
      NextSchedulationDate: startDate,
      EndDate: endDate,
      Schedulation: schedulation,
      EntityID: entityDetails.entity.EntityID,
      FilterItems: filterBuild.filterItems,
      QueryComposition: filterBuild.queryComposition,
      GlobalTimerange: existingAudience?.globalTimerange ?? 91,
      ExpandSourceGroupID: entityDetails.sourceGroupId,
      AudienceQuality: audienceQuality,
      ExtendVolumes: draftExtendVolumes,
      Tags: tags || undefined,
      SourceGroupIDs: Array.from(allSourceGroupIds),
      ExpandSimilarUsers: shouldExpandSimilarUsers,
      PreviewOnly: options?.previewOnly ? true : undefined,
      PreviewAccuracyStep: resetQualityIndex
        ? null
        : (formValue.previewAccuracyStep ?? formValue.previewData?.selectedStep ?? null),
      AudienceQualityIndex: audienceQualityIndex,
      ResetQualityIndex: resetQualityIndex ? true : undefined,
      FrontendPreview: {
        // In edit, l'AudienceType già salvato dal backend è la fonte di verità e non va mai
        // ricalcolato/riscritto dal frontend (vedi isCustomQueryAudience). Solo per una nuova
        // audience (nessun existingAudience) il tipo viene determinato dal form.
        AudienceType: existingAudience?.frontendPreview?.AudienceType
          ?? (isCustomQuery ? AUDIENCE_TYPES.CUSTOM_QUERY : isRuleBased ? AUDIENCE_TYPES.CUSTOM : AUDIENCE_TYPES.INTERESTS),
        // Modalità di espansione (admin-only). Il backend forza 'standard' se l'utente non è autorizzato.
        ExpandVersion: formValue.expandVersionWeighted ? 'weighted' : 'standard'
      }
    };

    return payload;
  }

  private resolveAudienceQualityIndex(
    formValue: AudienceCreateFormValue,
    existingAudience?: AudienceItem | null
  ): number | null {
    const previewData = formValue.previewData
      ?? (existingAudience?.previewAccuracy
        ? mapPreviewAccuracyToForm(existingAudience.previewAccuracy, existingAudience.audienceQualityIndex)
        : null);

    if (!previewData?.points?.length) {
      return null;
    }

    const rawStep = formValue.previewAccuracyStep ?? previewData.selectedStep ?? previewData.defaultStep ?? 0;
    const step = Math.min(Math.max(Number(rawStep) || 0, 0), Math.max(previewData.points.length - 1, 0));
    const point = previewData.points[step] ?? previewData.points[0];
    return point ? point.minDistance : null;
  }

  private isRuleBasedForm(formValue: AudienceCreateFormValue): boolean {
    return formValue.type === AUDIENCE_TYPES.CUSTOM;
  }

  private resolveExtendVolumes(formValue: AudienceCreateFormValue): number | null {
    const val = formValue.extendVolumes;
    if (val === null || val === undefined) return null;
    const num = Number(val);
    return isNaN(num) ? null : num;
  }

  private generateTags(description?: string | null): Observable<string[] | null> {
    const normalized = description?.toString().trim();
    if (!normalized) {
      return of(null);
    }
    return this.audienceApi.extractTags(normalized).pipe(
      map(tags => tags && tags.length ? tags : null),
      catchError((error) => {
        console.error('[audience] tag generation failed', error);
        return of(null);
      })
    );
  }

  async confirmCreateExit(): Promise<void> {
    this.createExitConfirmOpen = false;
    const resolver = this.pendingCreateExitResolver;
    const action = this.pendingCreateExitAction;
    const options = this.pendingCreateExitOptions;
    this.pendingCreateExitResolver = null;
    this.pendingCreateExitAction = null;
    this.pendingCreateExitOptions = null;
    if (!action) {
      resolver?.(true);
      return;
    }
    const shouldTemporarilySkip = options?.expectRouteChange ?? false;
    const previousSkipState = this.skipCreateExitWarning;
    if (shouldTemporarilySkip) {
      this.skipCreateExitWarning = true;
    }
    try {
      await action();
      resolver?.(true);
    } catch (error) {
      console.error('[audience] leave action failed', error);
      resolver?.(false);
    } finally {
      if (shouldTemporarilySkip) {
        this.skipCreateExitWarning = previousSkipState;
      }
    }
  }

  cancelCreateExit(): void {
    this.createExitConfirmOpen = false;
    const resolver = this.pendingCreateExitResolver;
    this.pendingCreateExitResolver = null;
    this.pendingCreateExitAction = null;
    this.pendingCreateExitOptions = null;
    if (resolver) {
      resolver(false);
    }
  }

  canDeactivateAudiencePage(): Promise<boolean> {
    return this.confirmCreateExitIfNeeded(undefined, { expectRouteChange: true });
  }

  onSseForceExitConfirm(): void {
    this.sseForceExitOpen = false;
    this.sseForceExitReason = null;
    this.skipCreateExitWarning = true;
    this.navigateBackToList();
  }

  private shouldBlockCreateExit(): boolean {
    if (!this.shouldProtectCreateExit || this.skipCreateExitWarning) {
      return false;
    }
    return this.audienceCreateForm?.hasPendingChanges() ?? false;
  }

  private openCreateExitConfirm(
    action?: () => void | Promise<void>,
    resolver?: (confirmed: boolean) => void,
    options?: NavigationBlockOptions
  ): void {
    this.pendingCreateExitAction = action ?? null;
    this.pendingCreateExitResolver = resolver ?? null;
    this.pendingCreateExitOptions = options ?? null;
    this.createExitConfirmOpen = true;
  }

  private closeCreateExitConfirm(): void {
    this.createExitConfirmOpen = false;
    this.pendingCreateExitAction = null;
    this.pendingCreateExitResolver = null;
    this.pendingCreateExitOptions = null;
  }

  private restoreCreateHistoryState(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.history.pushState({ audienceMode: 'create' }, '', window.location.pathname);
    window.sessionStorage.setItem(this.modeStorageKey, 'create');
  }

  private async confirmCreateExitIfNeeded(
    action?: () => void | Promise<void>,
    options?: NavigationBlockOptions
  ): Promise<boolean> {
    if (!this.shouldBlockCreateExit()) {
      if (action) {
        await action();
      }
      return true;
    }
    return await new Promise(resolve => {
      this.openCreateExitConfirm(action, resolve, options);
    });
  }

  private initializeModeState(): void {
    if (typeof window === 'undefined') {
      this.mode = 'list';
      return;
    }
    // Always start in list mode when navigating to audience page
    this.replaceHistoryState('list');
    window.sessionStorage.setItem(this.modeStorageKey, 'list');
    this.mode = 'list';
  }

  private activateCreateView(params: { creationMode: 'create' | 'edit' | 'clone'; initialValue: Partial<AudienceCreateFormValue> | null; editingId: string | null; editingIsPublic?: boolean | null; pushHistory: boolean; ownerEntityId?: string | null; ownerEntityType?: string | null }): void {
    this.mode = 'create';
    this.creationMode = params.creationMode;
    this.createInitialValue = params.initialValue;
    this.editingAudienceId = params.editingId;
    this._editOwnerEntityId = params.ownerEntityId ?? null;
    this._editOwnerEntityType = params.ownerEntityType ?? null;
    this.editingAudienceIsPublic = params.creationMode === 'edit' ? (params.editingIsPublic ?? null) : null;

    this.creationError = null;
    this.shouldProtectCreateExit = true;
    this.skipCreateExitWarning = false;
    this.closeCreateExitConfirm();
    this.navigationBlockService.register(this.createExitNavigationBlocker);
    // Load goal options for source group resolution at submit time
    if (!this.loadedGoalOptions.length) {
      firstValueFrom(this.audienceApi.getGoals('active').pipe(catchError(() => of<GoalOption[]>([]))))
        .then(goals => { this.loadedGoalOptions = goals; });
    }
    // Notify sidebar to disable entity selector
    this.creationStateService.setCreationMode(true);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(this.modeStorageKey, 'create');
      if (params.pushHistory) {
        window.history.pushState({ audienceMode: 'create' }, '', window.location.pathname);
      }
    }
  }

  private navigateBackToList(): void {
    if (typeof window !== 'undefined' && window.history.state?.audienceMode === 'create' && window.history.length > 1) {
      this.suppressNextPopState = true;
      window.history.back();
    } else {
      this.exitCreateView(true);
    }
  }

  private exitCreateView(replaceHistory: boolean): void {
    this.mode = 'list';
    this.creationMode = 'create';
    this.createInitialValue = null;
    this.editingAudienceId = null;
    this._editOwnerEntityId = null;
    this._editOwnerEntityType = null;
    this.editingAudienceIsPublic = null;
    this.creationError = null;
    this.obsoleteFiltersInEdit = [];
    this.shouldProtectCreateExit = false;
    this.skipCreateExitWarning = false;
    this.closeCreateExitConfirm();
    this.navigationBlockService.clear(this.createExitNavigationBlocker);
    // Notify sidebar to enable entity selector
    this.creationStateService.setCreationMode(false);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(this.modeStorageKey, 'list');
      if (replaceHistory) {
        this.replaceHistoryState('list');
      }
    }
  }

  private replaceHistoryState(mode: 'list' | 'create'): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.history.replaceState({ audienceMode: mode }, '', window.location.pathname);
  }

  private formatExpectedVolumeInput(volume?: number | null): string {
    if (!volume || Number.isNaN(volume)) {
      return '';
    }
    return Math.round(volume).toString();
  }

  private parseNumeric(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  }

  private resolveEntity(
    user: SessionUser | null,
    preferredSourceGroupId?: string | null
  ): { entity: { EntityType: string; EntityID: string }; sourceGroupId: string } | null {
    if (!user) {
      return null;
    }
    // IMPORTANT: Use the preferredSourceGroupId (from getAudienceCreationDcrId) as primary source
    // This ensures we use the DCR selected in the sidebar, not the user's legacy entityId field
    const entityId = preferredSourceGroupId || user.entityID;
    if (!entityId) {
      return null;
    }
    const entityType = user.dataAccessLevel ?? 'publisher';
    return {
      entity: {
        EntityID: entityId,
        EntityType: entityType
      },
      sourceGroupId: preferredSourceGroupId || entityId
    };
  }

  private resolveExistingEntity(
    audience: AudienceItem,
    fallback?: { entity: { EntityType: string; EntityID: string }; sourceGroupId: string } | null,
    preferredSourceGroupId?: string | null
  ): { entity: { EntityType: string; EntityID: string }; sourceGroupId: string } | null {
    const entityId = audience.entityId || fallback?.entity.EntityID;
    if (!entityId) {
      return fallback ?? null;
    }
    const entityType = audience.entityType || fallback?.entity.EntityType || 'publisher';
    return {
      entity: {
        EntityID: entityId,
        EntityType: entityType
      },
      sourceGroupId: preferredSourceGroupId || audience.expandSourceGroupId || fallback?.sourceGroupId || entityId
    };
  }

  private resolveSelectedEntityName(): string {
    const activeId = this.authService.getActiveDcrId();
    if (!activeId) {
      return this.translate.instant('common.allEntities');
    }
    return this.authService.getDcrNameById(activeId) || activeId;
  }

  private findFirstPartyGroupId(dcrId: string): string | null {
    if (!dcrId) {
      return null;
    }
    const normalizedDcrId = dcrId.trim();
    for (const group of this.sourceGroupMap.values()) {
      const groupType = (group.Type || '').toString().toLowerCase();
      if (groupType === 'first-party') {
        const ownerDcrId = (group.DCR_ID || '').toString().trim();
        if (ownerDcrId === normalizedDcrId) {
          return group.ID;
        }
        const dcrIds = group.DCR_IDs || [];
        if (dcrIds.some(id => id && id.toString().trim() === normalizedDcrId)) {
          return group.ID;
        }
      }
    }
    return null;
  }

  private async ensureSourceGroupsLoaded(): Promise<void> {
    if (this.sourceGroupMap.size > 0) {
      return;
    }
    try {
      const groups = await firstValueFrom(this.groupsApi.getGroups());
      this.sourceGroupMap = new Map(groups.map(group => [group.ID, group]));
    } catch (error) {
      console.error('[audience] Failed to load source groups for payload build', error);
    }
  }

  private resolveSourceFilterGroupIds(sourceEntityIds: Iterable<string>): string[] {
    const sourceGroupIds = new Set<string>();

    for (const rawId of sourceEntityIds) {
      const entityId = rawId?.toString().trim();
      if (!entityId) {
        continue;
      }

      // If the filter already carries a first-party group ID, preserve it as-is.
      if (this.sourceGroupMap.has(entityId)) {
        sourceGroupIds.add(entityId);
        continue;
      }

      const provider = this.providerMap.get(entityId);
      const candidateDcrIds = new Set<string>();

      if (provider?.PublisherID?.toString().trim()) {
        candidateDcrIds.add(provider.PublisherID.toString().trim());
      }
      if (provider?.ID?.toString().trim()) {
        candidateDcrIds.add(provider.ID.toString().trim());
      }

      // SOURCE filters can also be serialized as publisher entities.
      candidateDcrIds.add(entityId);

      candidateDcrIds.forEach(candidateId => {
        const groupId = this.findFirstPartyGroupId(candidateId);
        if (groupId) {
          sourceGroupIds.add(groupId);
        }
      });
    }

    return Array.from(sourceGroupIds);
  }

  private async extractSourceGroupIDsFromFilters(filterItems: AudienceFilterItem[]): Promise<string[]> {
    const sourceGroupIds = new Set<string>();

    // Extract IDs from filters
    const goalIds = new Set<string>();
    const audienceIds = new Set<string>();
    const customImportIds = new Set<string>();
    const sourceProviderIds = new Set<string>();

    filterItems.forEach(item => {
      const normalizedType = normalizeFilterType(item.filterType);

      if (normalizedType === FILTER_TYPES.SOURCE.toLowerCase() ||
          normalizedType === FILTER_TYPES.EXCLUDED_SOURCE.toLowerCase()) {
        // SOURCE filters: extract PublisherIDs from entity objects
        const ids = extractEntityIdsFromFilterValue(item.filterValue);
        ids.forEach(id => sourceProviderIds.add(id));
      } else if (normalizedType === FILTER_TYPES.CUSTOM_IMPORT.toLowerCase()) {
        // CUSTOM_IMPORT filters: extract custom import IDs
        const ids = coerceFilterValueToArray(item.filterValue);
        ids.forEach(id => customImportIds.add(id));
      } else if (normalizedType === FILTER_TYPES.GOAL.toLowerCase()) {
        // GOAL filters: extract goal IDs
        const ids = coerceFilterValueToArray(item.filterValue);
        ids.forEach(id => goalIds.add(id));
      } else if (normalizedType === FILTER_TYPES.AUDIENCE.toLowerCase()) {
        // AUDIENCE filters: extract audience IDs
        const ids = coerceFilterValueToArray(item.filterValue);
        ids.forEach(id => audienceIds.add(id));
      }
    });

    // 1. Handle SOURCE filters: get PublisherID from providerMap and find first-party group
    for (const providerId of sourceProviderIds) {
      const provider = this.providerMap.get(providerId);
      if (provider?.PublisherID) {
        const groupId = this.findFirstPartyGroupId(provider.PublisherID);
        if (groupId) {
          sourceGroupIds.add(groupId);
        } else {
          throw new Error(this.translate.instant('audiences.create.errors.missingFirstPartyGroup', {
            provider: provider.Name || providerId
          }));
        }
      }
    }

    // 2. Handle CUSTOM_IMPORT filters: get OwnerEntityID and find first-party group
    if (customImportIds.size > 0) {
      await this.ensureSourceGroupsLoaded();
      const customImports = this.customImportOptions.filter(opt => customImportIds.has(opt.id));
      // We need the full CustomImportDto to get OwnerEntityID, but we only have { id, name }
      // So we fetch from the loaded data if available, otherwise we skip
      // For now, let's try to get from the customImportApi
      try {
        const allImports = await firstValueFrom(this.customImportApi.getCustomImports());
        const relevantImports = allImports.filter(imp => customImportIds.has(imp.ID));
        relevantImports.forEach(imp => {
          if (imp.OwnerEntityID) {
            const groupId = this.findFirstPartyGroupId(imp.OwnerEntityID);
            if (groupId) {
              sourceGroupIds.add(groupId);
            }
          }
        });
      } catch (error) {
        console.error('[audience] Failed to fetch custom imports for source groups', error);
      }
    }

    // 3. Handle GOAL filters: lookup SourceGroupID from already-loaded goal options
    if (goalIds.size > 0) {
      await this.ensureSourceGroupsLoaded();
    }
    goalIds.forEach(id => {
      const goal = this.loadedGoalOptions.find(g => g.ID === id);
      if (!goal?.SourceGroupID) {
        throw new Error(this.translate.instant('audiences.create.errors.missingGoalSourceGroup', {
          goal: goal?.Description || id
        }));
      }
      sourceGroupIds.add(goal.SourceGroupID);
    });

    // 4. Handle AUDIENCE filters (included only): lookup SourceGroupIDs from already-loaded audiences
    audienceIds.forEach(id => {
      const audience = this.audiences.find(a => a.id === id);
      audience?.sourceGroupIds?.forEach(sgId => { if (sgId) sourceGroupIds.add(sgId); });
      // If the included audience expands to similar users, its expansion group is a data
      // source for this audience too, so it must be inherited as well.
      if (audience?.expandSimilarUsers && audience.expandSourceGroupId) {
        sourceGroupIds.add(audience.expandSourceGroupId);
      }
    });

    return Array.from(sourceGroupIds);
  }

  private applyDestinationNames(audience: AudienceItem): AudienceItem {
    return {
      ...audience,
      destinations: audience.destinations.map(dest => {
        if (!dest.destinationId) {
          return dest;
        }
        const resolvedName = this.destinationNames.get(dest.destinationId);
        const resolvedType = this.destinationTypeInfo.get(dest.destinationId);
        return {
          ...dest,
          ...(resolvedName ? { name: resolvedName } : {}),
          ...(resolvedType ? { destinationTypeId: resolvedType.typeId, destinationTypeName: resolvedType.typeName } : {}),
        };
      })
    };
  }

  private ensureSelectedAudienceVisible(): void {
    const visible = this.computeFilteredAudiences();
    if (!visible.length) {
      this.selectedAudience = null;
      return;
    }
    if (this.selectedAudience) {
      const idx = visible.findIndex(aud => aud.id === this.selectedAudience?.id);
      if (idx >= 0) {
        this.currentPage = Math.floor(idx / this.pageSize);
        return;
      }
    }
    this.selectedAudience = visible[0];
  }

  private computeFilteredAudiences(): AudienceItem[] {
    const normalizedSearch = this.searchTerm.toLowerCase().trim();
    return [...this.audiences]
      .filter((audience) => this.audiencePassesStatusFilters(audience))
      .filter((audience) => this.matchesSearch(audience, normalizedSearch))
      .filter((audience) => this.audiencePassesOwnerFilter(audience))
      .sort((a, b) => {
        let comparison = 0;

        if (this.sortColumn === 'name') {
          // Sort alphabetically by name
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
        } else if (this.sortColumn === 'volume') {
          // Sort numerically by volume
          const volumeA = parseVolume(a.volume);
          const volumeB = parseVolume(b.volume);
          comparison = volumeA - volumeB;
        }

        return this.sortDirection === 'asc' ? comparison : -comparison;
      });
  }

  private getAvailableDestinations(audience: AudienceItem | null): DestinationDto[] {
    if (!audience) {
      return [];
    }
    const accessibleEntityId = this.getAccessibleDestinationEntityId();
    const activeDestinationIds = new Set(
      audience.destinations
        .map(dest => dest.destinationId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );
    return this.availableDestinations.filter(destination => {
      if (!destination || activeDestinationIds.has(String(destination.ID))) {
        return false;
      }
      if (accessibleEntityId && destination.EntityID) {
        const destinationEntityId = this.normalizeEntityId(destination.EntityID);
        if (destinationEntityId && destinationEntityId !== accessibleEntityId) {
          return false;
        }
      }
      return true;
    });
  }

  private getAccessibleDestinationEntityId(): string | null {
    if (this.hasGlobalDataAccess) {
      return null;
    }

    if (this.authService.isTNCDataSelection()) {
      return null;
    }

    const selectedEntityId = this.normalizeEntityId(this.authService.getActiveDcrId());

    if (!selectedEntityId) {
      return null;
    }

    const TNCDataEntityId = this.normalizeEntityId(environment.TNCDataEntityId);
    return selectedEntityId === TNCDataEntityId ? null : selectedEntityId;
  }

  private filterDestinations(destinations: DestinationDto[], term: string): DestinationDto[] {
    const normalized = term?.toString().trim().toLowerCase();
    if (!normalized) {
      return destinations;
    }
    return destinations.filter(destination => {
      const name = destination?.Name?.toLowerCase() || '';
      const connector = destination?.DestinationTypeName?.toLowerCase() || '';
      const activation = destination?.ActivationType?.toLowerCase() || '';
      return name.includes(normalized) || connector.includes(normalized) || activation.includes(normalized);
    });
  }

  private buildActivationChannelData(destination: DestinationDto, audience: AudienceItem): Record<string, unknown> | null {
    const ExternalSegmentID = audience.id || audience.name;
    const description = audience.description || audience.name;
    const activationType = (destination.ActivationType || '').toLowerCase();
    if (activationType === 'thetradedesk') {
      return {
        ExternalSegmentID: ExternalSegmentID,
        CustomAudienceName: audience.name?.slice(0, 80) || ExternalSegmentID,
        CustomAudienceDesc: description?.slice(0, 256) || audience.name,
        ExternalSegmentParentID: destination.EntityID || audience.entityId || ExternalSegmentID,
        Buyable: true,
        CPMRateAmount: 0,
        CPMRateCurrencyCode: 'EUR',
        PercentOfMediaCostRate: 0,
        RateLevel: 'System'
      };
    }
    if (activationType === 'audiencerealtime') {
      const ownerId = audience.entityId || destination.EntityID || this.authService.getActiveDcr();
      if (!ownerId) {
        return null;
      }
      return {
        ExternalSegmentID: ExternalSegmentID,
        ExternalAudienceName: audience.name,
        Description: description,
        OwnerID: ownerId,
        TTL: 91
      };
    }
    return {
      ExternalSegmentID: ExternalSegmentID
    };
  }

  private filterOverlapResults(results: AudienceIntersection[], term: string): AudienceIntersection[] {
    const normalized = term?.toString().trim().toLowerCase();
    if (!normalized) {
      return results;
    }
    return results.filter(result =>
      result.targetAudienceName?.toLowerCase().includes(normalized) ||
      result.targetOwnerLabel?.toLowerCase().includes(normalized)
    );
  }

  resolveOwnerLabel(entityType?: string | null, entityId?: string | null): string {
    const normalizedId = (entityId ?? '').toString().trim();
    const normalizedType = (entityType ?? '').toString().trim().toLowerCase();
    if (!normalizedId) {
      return '—';
    }

    // Try cache with entityType if available
    const key = normalizedType ? `${normalizedType}::${normalizedId}` : null;
    if (key) {
      const cached = this.ownerLabels.get(key);
      if (cached) {
        return cached;
      }
    }

    // Try cache with 'publisher' prefix as fallback
    if (!normalizedType || normalizedType !== 'publisher') {
      const publisherKey = `publisher::${normalizedId}`;
      const publisherCached = this.ownerLabels.get(publisherKey);
      if (publisherCached) {
        return publisherCached;
      }
    }

    // Try to resolve from authService DCR list
    const name = this.authService.getDcrNameById(normalizedId);
    if (name && name !== normalizedId) {
      return name;
    }

    // Fallback to ID
    if (!entityType) {
      return normalizedId;
    }
    return `${entityType} • ${normalizedId}`;
  }

  private resetPagination(): void {
    this.currentPage = 0;
  }

  /**
   * Used when the list is re-sorted or re-filtered: always land on page 1. Unlike
   * ensureSelectedAudienceVisible, it does not follow the selected audience to its
   * new page (after sorting by size the auto-selected, alphabetically-first audience
   * would drag the list to e.g. page 20/41); if the selection is not on page 1,
   * the first row is selected instead.
   */
  private resetToFirstPage(): void {
    this.resetPagination();
    const firstPage = this.paginatedAudiences;
    if (!firstPage.length) {
      this.selectedAudience = null;
      return;
    }
    if (!firstPage.some(aud => aud.id === this.selectedAudience?.id)) {
      this.selectedAudience = firstPage[0];
    }
  }

  private closeOwnerFilterPanel(): void {
    this.ownerFilterOpen = false;
  }

  private updateOwnerFilterPosition(anchor: HTMLElement): void {
    if (typeof window === 'undefined') {
      this.ownerFilterPosition = { top: 0, left: 0 };
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const panelWidth = 320;
    const padding = 16;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const left = Math.min(Math.max(rect.left, padding), viewportWidth - panelWidth - padding);
    this.ownerFilterPosition = {
      top: rect.bottom + 8,
      left
    };
  }

  private resolveResumeEndDate(audience: AudienceItem): string {
    const normalizedEnd = normalizeDate(audience.endDateRaw);
    if (normalizedEnd) {
      return normalizedEnd;
    }
    const normalizedStart = normalizeDate(audience.nextExtractionRaw) || todayIsoDate();
    return addDays(normalizedStart, 365);
  }

  private enrichOverlapResults(result: AudienceIntersection[]): AudienceIntersection[] {
    // The backend already restricts targets to what the user may compare against
    // (audiences of the current entity + public audiences of any entity), so we no
    // longer re-filter here against the page's audience list. That cross-reference
    // silently dropped valid targets — e.g. a public source vs a private target of
    // the current entity, and any target not present in the loaded list. We only
    // enrich each row with the owner label resolved from the target's EntityID.
    return result.map(intersection => ({
      ...intersection,
      targetOwnerLabel: this.resolveOwnerLabel(null, intersection.targetEntityId)
    }));
  }

  closeOverlapModal(): void {
    this.overlapModalOpen = false;
    this.overlapLoadingId = null;
  }

}
